import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  Clock, 
  Banknote, 
  User,
  Check,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Minus,
  Plus,
  Camera,
  Package,
  MapPin,
} from "lucide-react";
import { format, addDays, startOfDay, isSameDay, isBefore, addMinutes } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { MapPreview } from "@/components/maps/MapPreview";
import { PlacesAutocompleteInput } from "@/components/maps/PlacesAutocompleteInput";
import { themeVarsFromHex } from "@/lib/theme";
import PhoneInput, { type Country } from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { buildMultiServiceAddonPayload } from "@/lib/multi-service-utils";
import { buildPaystackCallbackUrl } from "@/lib/payments";
import {
  customerPaystackChargeAmount,
  customerPlatformBookingFeeAmount,
} from "@/lib/platform-settlements";
import { businessAllowsCustomerPayments, businessUsesOnlineCheckout } from "@/lib/billing-model";
import { sendOwnerPush } from "@/lib/owner-push";
import { notifyExploreCustomerBooking } from "@/lib/explore-booking-notify";
import { getExploreDeviceId } from "@/lib/explore-push";
import { isExploreAppVariant } from "@/lib/platform";
import { getBaseUrl } from "@/lib/site";
import {
  areServicesAvailableOnDay,
  doesSlotFitServiceHours,
  minutesToTime,
  timeToMinutes,
} from "@/lib/service-hours";
import { ServiceGallery, type MediaItem } from "@/components/ServiceGallery";
import { SocialMediaIcons } from "@/components/SocialMediaIcons";
import { LogoStack } from "@/components/LogoStack";

type BookingStep = "service" | "staff" | "datetime" | "details" | "confirm" | "success";

type Business = Tables<"businesses">;
type Service = Tables<"services">;
type ServiceCategory = Tables<"service_categories">;
type ServiceHour = Tables<"service_hours">;
type Staff = Tables<"staff">;
type Location = Tables<"locations">;
type ServiceAddon = Tables<"service_addons">;

const HERO_CROSSFADE_MS = 520;
const CARD_CROSSFADE_MS = 360;
const SIMPLE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeBookingPhone(phoneE164: string): string | null {
  const raw = String(phoneE164 || "").trim();
  if (!raw) return null;
  const parsed = parsePhoneNumberFromString(raw);
  return parsed?.isValid() ? parsed.number : null;
}

function getBookingPhoneValidationError(phoneE164: string): string | null {
  if (!String(phoneE164 || "").trim()) return "Phone number is required.";
  return normalizeBookingPhone(phoneE164) ? null : "Enter a valid phone number.";
}

function normalizeOptionalEmail(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  return normalized || null;
}

function getBookingEmailValidationError(email: string, isRequired: boolean): string | null {
  const normalized = normalizeOptionalEmail(email);
  if (!normalized) {
    return isRequired ? "Email is required to pay online." : null;
  }
  return SIMPLE_EMAIL_RE.test(normalized) ? null : "Enter a valid email address.";
}

/** Preload next/prev hero assets to reduce wait before crossfade */
function usePreloadAdjacentMedia(items: MediaItem[], index: number) {
  useEffect(() => {
    if (items.length <= 1) return;
    const n = items.length;
    const adjacent = [items[(index + 1) % n], items[(index - 1 + n) % n]];
    const cleanups: Array<() => void> = [];

    for (const m of adjacent) {
      if (!m?.url) continue;
      if (m.type === "image") {
        const img = new Image();
        img.src = m.url;
      } else {
        const v = document.createElement("video");
        v.preload = "auto";
        v.src = m.url;
        cleanups.push(() => {
          v.removeAttribute("src");
          v.load();
        });
      }
    }

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [items, index]);
}

function HeroCrossfadeMedia({
  items,
  index,
  prefersReducedMotion,
  heroHasMultiple,
  onVideoEnded,
}: {
  items: MediaItem[];
  index: number;
  prefersReducedMotion: boolean;
  heroHasMultiple: boolean;
  onVideoEnded: () => void;
}) {
  const [stableIndex, setStableIndex] = useState(index);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const [incomingReady, setIncomingReady] = useState(false);
  const [fadeIncomingIn, setFadeIncomingIn] = useState(false);
  const fadeTimerRef = useRef<number | null>(null);

  usePreloadAdjacentMedia(items, stableIndex);

  useEffect(() => {
    if (index === stableIndex) return;
    if (prefersReducedMotion) {
      setStableIndex(index);
      setPendingIndex(null);
      setIncomingReady(false);
      setFadeIncomingIn(false);
      return;
    }
    setPendingIndex(index);
    setIncomingReady(false);
    setFadeIncomingIn(false);
  }, [index, stableIndex, prefersReducedMotion]);

  useEffect(() => {
    return () => {
      if (fadeTimerRef.current != null) window.clearTimeout(fadeTimerRef.current);
    };
  }, []);

  const outgoing = items[stableIndex] || null;
  const incoming = pendingIndex != null ? items[pendingIndex] || null : null;

  const commitTransition = () => {
    if (pendingIndex == null) return;
    setFadeIncomingIn(true);
    if (fadeTimerRef.current != null) window.clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = window.setTimeout(() => {
      setStableIndex(pendingIndex);
      setPendingIndex(null);
      setIncomingReady(false);
      setFadeIncomingIn(false);
      fadeTimerRef.current = null;
    }, HERO_CROSSFADE_MS);
  };

  const onIncomingAssetReady = () => {
    if (prefersReducedMotion || pendingIndex == null) return;
    if (incomingReady) return;
    setIncomingReady(true);
    requestAnimationFrame(() => commitTransition());
  };

  const onIncomingError = () => {
    if (prefersReducedMotion || pendingIndex == null) return;
    if (incomingReady) return;
    setIncomingReady(true);
    requestAnimationFrame(() => commitTransition());
  };

  if (!outgoing && !incoming) {
    return <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />;
  }

  const showPair = incoming && !prefersReducedMotion && pendingIndex != null;

  return (
    <>
      {/* Outgoing layer (hidden when reduced-motion path renders below) */}
      {!prefersReducedMotion && outgoing ? (
        <div
          className={cn(
            "absolute inset-0 transition-opacity ease-out",
            showPair && fadeIncomingIn ? "opacity-0 z-[1]" : "opacity-100 z-[1]",
          )}
          style={{ transitionDuration: `${HERO_CROSSFADE_MS}ms` }}
        >
          {outgoing.type === "video" ? (
            <video
              key={`out-${stableIndex}-${outgoing.url}`}
              src={outgoing.url}
              className="h-full w-full object-cover object-center"
              muted
              playsInline
              autoPlay={!prefersReducedMotion}
              preload="auto"
              loop={!heroHasMultiple}
              onEnded={() => {
                if (!heroHasMultiple) return;
                onVideoEnded();
              }}
              onError={() => {
                if (!heroHasMultiple) return;
                onVideoEnded();
              }}
            />
          ) : (
            <img
              key={`out-img-${stableIndex}-${outgoing.url}`}
              src={outgoing.url}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover object-center"
              loading="eager"
              decoding="async"
            />
          )}
        </div>
      ) : null}

      {/* Incoming layer (crossfade target) */}
      {incoming && showPair ? (
        <div
          className={cn(
            "absolute inset-0 z-[2] transition-opacity ease-out",
            fadeIncomingIn ? "opacity-100" : "opacity-0",
          )}
          style={{ transitionDuration: `${HERO_CROSSFADE_MS}ms` }}
        >
          {incoming.type === "video" ? (
            <video
              key={`in-${pendingIndex}-${incoming.url}`}
              src={incoming.url}
              className="h-full w-full object-cover object-center"
              muted
              playsInline
              autoPlay={!prefersReducedMotion}
              preload="auto"
              loop={!heroHasMultiple}
              onLoadedData={onIncomingAssetReady}
              onCanPlay={onIncomingAssetReady}
              onEnded={() => {
                if (!heroHasMultiple) return;
                onVideoEnded();
              }}
              onError={onIncomingError}
            />
          ) : (
            <img
              key={`in-img-${pendingIndex}-${incoming.url}`}
              src={incoming.url}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover object-center"
              loading="eager"
              decoding="async"
              onLoad={onIncomingAssetReady}
              onError={onIncomingError}
            />
          )}
        </div>
      ) : null}

      {/* Reduced motion: single layer only */}
      {prefersReducedMotion && outgoing ? (
        <div className="absolute inset-0 z-[1]">
          {outgoing.type === "video" ? (
            <video
              src={outgoing.url}
              className="h-full w-full object-cover object-center"
              muted
              playsInline
              autoPlay={false}
              preload="metadata"
              loop={!heroHasMultiple}
              onEnded={() => {
                if (!heroHasMultiple) return;
                onVideoEnded();
              }}
            />
          ) : (
            <img
              src={outgoing.url}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover object-center"
              loading="eager"
            />
          )}
        </div>
      ) : null}
    </>
  );
}

function ServiceCardMedia({
  mediaItems,
  alt,
  onClick,
}: {
  mediaItems: MediaItem[];
  alt: string;
  onClick: () => void;
}) {
  const [idx, setIdx] = useState(0);
  const [crossfade, setCrossfade] = useState<{ from: number; to: number } | null>(null);
  const [runTransition, setRunTransition] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const hasMultiple = mediaItems.length > 1;
  const n = mediaItems.length;

  const advance = useCallback(() => {
    if (!hasMultiple || n === 0) return;
    setIdx((prev) => {
      const next = (prev + 1) % n;
      if (!prefersReducedMotion) {
        setCrossfade({ from: prev, to: next });
      }
      return next;
    });
  }, [hasMultiple, n, prefersReducedMotion]);

  useEffect(() => {
    if (!crossfade) return;
    setRunTransition(false);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setRunTransition(true));
    });
    return () => cancelAnimationFrame(id);
  }, [crossfade]);

  useEffect(() => {
    if (!crossfade) return;
    const t = window.setTimeout(() => {
      setCrossfade(null);
      setRunTransition(false);
    }, CARD_CROSSFADE_MS);
    return () => window.clearTimeout(t);
  }, [crossfade]);

  const current = mediaItems[idx];
  const fromItem = crossfade != null ? mediaItems[crossfade.from] : null;
  const toItem = crossfade != null ? mediaItems[crossfade.to] : null;

  // Auto-advance images after a delay (videos advance via onEnded)
  useEffect(() => {
    if (!hasMultiple || !current) return;
    if (current.type === "video") return;

    const timeout = window.setTimeout(advance, 5000);
    return () => window.clearTimeout(timeout);
  }, [current, hasMultiple, idx, advance]);

  const handleVideoEnded = () => {
    advance();
  };

  if (!current) return null;

  const showCrossfade = Boolean(crossfade && fromItem && toItem && !prefersReducedMotion);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-muted relative group cursor-pointer",
      )}
    >
      <div className="absolute inset-0">
        {showCrossfade && fromItem && toItem ? (
          <>
            <div
              className={cn(
                "absolute inset-0 z-[1] transition-opacity ease-out",
                runTransition ? "opacity-0" : "opacity-100",
              )}
              style={{ transitionDuration: `${CARD_CROSSFADE_MS}ms` }}
            >
              {fromItem.type === "video" ? (
                <video
                  src={fromItem.url}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img src={fromItem.url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              )}
            </div>
            <div
              className={cn(
                "absolute inset-0 z-[2] transition-opacity ease-out",
                runTransition ? "opacity-100" : "opacity-0",
              )}
              style={{ transitionDuration: `${CARD_CROSSFADE_MS}ms` }}
            >
              {toItem.type === "video" ? (
                <video
                  key={`card-to-${crossfade.to}-${toItem.url}`}
                  src={toItem.url}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                  autoPlay
                  loop={!hasMultiple}
                  preload="metadata"
                  onEnded={handleVideoEnded}
                />
              ) : (
                <img
                  key={`card-img-to-${crossfade.to}-${toItem.url}`}
                  src={toItem.url}
                  alt={alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              )}
            </div>
          </>
        ) : (
          <div className="absolute inset-0 z-[1]">
            {current.type === "video" ? (
              <video
                src={current.url}
                className="w-full h-full object-cover"
                muted
                playsInline
                autoPlay
                loop={!hasMultiple}
                preload="metadata"
                onEnded={handleVideoEnded}
              />
            ) : (
              <img src={current.url} alt={alt} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            )}
          </div>
        )}
      </div>

      {hasMultiple && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
            {mediaItems.length} photos
          </div>
        </div>
      )}
    </div>
  );
}

interface StaffOption {
  id: string;
  name: string;
  email?: string;
}

// Helper function to get currency symbol
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    GHS: "₵",
    NGN: "₦",
    USD: "$",
    EUR: "€",
    GBP: "£",
    ZAR: "R",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

const formatDuration = (minutes: number): string => {
  const total = Math.max(0, Math.round(Number(minutes || 0)));
  if (total < 60) return `${total} ${total === 1 ? "min" : "mins"}`;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  const hourPart = `${hours} ${hours === 1 ? "hour" : "hours"}`;
  if (mins === 0) return hourPart;
  const minPart = `${mins} ${mins === 1 ? "min" : "mins"}`;
  return `${hourPart} ${minPart}`;
};

// Helper function to generate time slots based on business hours (fixed intervals)
const generateTimeSlots = (openTime: string, closeTime: string, intervalMinutes: number = 30): string[] => {
  const slots: string[] = [];
  const [openHour, openMinute] = openTime.split(":").map(Number);
  const [closeHour, closeMinute] = closeTime.split(":").map(Number);
  
  let currentMinutes = openHour * 60 + openMinute;
  const closeMinutes = closeHour * 60 + closeMinute;
  
  while (currentMinutes < closeMinutes) {
    const hours = Math.floor(currentMinutes / 60);
    const minutes = currentMinutes % 60;
    slots.push(`${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`);
    currentMinutes += intervalMinutes;
  }
  
  return slots;
};

// Parse PostgREST timestamps robustly (we've seen formats like ISO and "YYYY-MM-DD HH:mm:ss+00")
// Returns milliseconds since epoch, or null if unparseable.
const safeParseDateTimeMs = (value: unknown): number | null => {
  if (!value) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value !== "string") return null;

  // Fast path: ISO-8601 (what PostgREST usually returns)
  let ms = new Date(value).getTime();
  if (Number.isFinite(ms)) return ms;

  // Common fallback: "YYYY-MM-DD HH:mm:ss+00" or "YYYY-MM-DD HH:mm:ss"
  let s = value.trim();
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(s)) {
    s = s.replace(/\s+/, "T");
  }
  // Normalize timezone like +00 or +0000 → +00:00
  s = s.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
  s = s.replace(/([+-]\d{2})$/, "$1:00");
  // If still no timezone info, assume UTC to avoid local-time parsing quirks
  if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(s)) s = `${s}Z`;

  ms = new Date(s).getTime();
  return Number.isFinite(ms) ? ms : null;
};

// Dynamic slot generation: generates slots starting after appointments end (including buffers)
// Then continues with 30-minute intervals from that point
const generateDynamicTimeSlots = (
  openTime: string,
  closeTime: string,
  selectedDate: Date,
  existingAppointments: any[],
  breakTimes: any[],
  selectedService: Service | null,
  selectedStaff: StaffOption | null,
  selectedLocationId: string | null,
  intervalMinutes: number = 30
): string[] => {
  const slots: string[] = [];
  const [openHour, openMinute] = openTime.split(":").map(Number);
  const [closeHour, closeMinute] = closeTime.split(":").map(Number);
  
  const dayStart = new Date(selectedDate);
  dayStart.setHours(openHour, openMinute, 0, 0);
  const dayEnd = new Date(selectedDate);
  dayEnd.setHours(closeHour, closeMinute, 0, 0);
  
  // Get break times for this day
  const dayOfWeek = selectedDate.getDay();
  const dayBreaks = breakTimes.filter((bt: any) => bt.day_of_week === dayOfWeek);
  
  // Calculate when each existing appointment actually ends (including buffer after)
  const blockedPeriods: Array<{ startMs: number; endMs: number }> = [];
  
  existingAppointments.forEach((apt: any) => {
    // If location is selected, only consider appointments for same location (or null location).
    // Null location appointments are treated as "global" (older data / single-location setups) and block all locations.
    if (selectedLocationId && apt.location_id && apt.location_id !== selectedLocationId) return;
    
    // If staff is selected, only consider appointments for same staff
    if (selectedStaff && selectedStaff.id !== "any" && apt.staff_id) {
      if (apt.staff_id !== selectedStaff.id) {
        return; // Different staff, ignore
      }
    }
    
    const aptStartMs = safeParseDateTimeMs(apt.start_datetime);
    const aptEndMs = safeParseDateTimeMs(apt.end_datetime);
    
    if (aptStartMs == null || aptEndMs == null) {
      return;
    }
    
    // Apply buffer times to the appointment period
    // The appointment end_datetime is service duration, but doesn't include buffer_after
    // buffer_before should be applied to the start time
    const service = apt.services;
    let actualStartMs = aptStartMs;
    let actualEndMs = aptEndMs;
    
    if (service) {
      // Subtract buffer_before from start time
      if (service.buffer_before_minutes) {
        actualStartMs = actualStartMs - service.buffer_before_minutes * 60_000;
      }
      // Add buffer_after to end time
      if (service.buffer_after_minutes) {
        actualEndMs = actualEndMs + service.buffer_after_minutes * 60_000;
      }
    }
    
    // Guard against weird data
    if (!Number.isFinite(actualStartMs) || !Number.isFinite(actualEndMs) || actualEndMs <= actualStartMs) return;
    
    blockedPeriods.push({ startMs: actualStartMs, endMs: actualEndMs });
  });
  
  // Add break times as blocked periods
  dayBreaks.forEach((breakTime: any) => {
    const [breakStartHour, breakStartMin] = breakTime.start_time.split(":").map(Number);
    const [breakEndHour, breakEndMin] = breakTime.end_time.split(":").map(Number);
    const breakStart = new Date(selectedDate);
    breakStart.setHours(breakStartHour, breakStartMin, 0, 0);
    const breakEnd = new Date(selectedDate);
    breakEnd.setHours(breakEndHour, breakEndMin, 0, 0);
    const startMs = breakStart.getTime();
    const endMs = breakEnd.getTime();
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      blockedPeriods.push({ startMs, endMs });
    }
  });
  
  // Sort blocked periods by start time
  blockedPeriods.sort((a, b) => a.startMs - b.startMs);
  
  let currentTime = new Date(dayStart);
  const now = new Date();
  const minimumBookingTime = isSameDay(selectedDate, now) ? addMinutes(now, 15) : null;
  
  while (currentTime < dayEnd) {
    // Skip if too soon (for today)
    if (minimumBookingTime && isBefore(currentTime, minimumBookingTime)) {
      currentTime = addMinutes(currentTime, intervalMinutes);
      continue;
    }

    // Ensure the candidate slot (including buffers) fits within business hours
    if (selectedService) {
      const bufferBefore = selectedService.buffer_before_minutes || 0;
      const bufferAfter = selectedService.buffer_after_minutes || 0;
      const duration = selectedService.duration_minutes || 30;
      const slotStartWithBuffer = addMinutes(currentTime, -bufferBefore);
      const slotEndWithBuffer = addMinutes(currentTime, duration + bufferAfter);

      // If buffer_before would start before open time, jump forward to the earliest valid start
      if (slotStartWithBuffer < dayStart) {
        currentTime = addMinutes(dayStart, bufferBefore);
        continue;
      }

      // If the service+buffer_after would end after close time, nothing else will fit today
      if (slotEndWithBuffer > dayEnd) {
        break;
      }
    } else {
      // No service selected: ensure the minimum assumed duration fits
      const slotEnd = addMinutes(currentTime, 30);
      if (slotEnd > dayEnd) {
        break;
      }
    }
    
    // Check if current time conflicts with any blocked period
    let hasConflict = false;
    let nextAvailableTimeMs: number | null = null;
    
    for (const blocked of blockedPeriods) {
      // Calculate what the slot would be if booked at currentTime
      let slotEndMs: number;
      if (selectedService) {
        const duration = selectedService.duration_minutes || 30;
        const bufferBefore = selectedService.buffer_before_minutes || 0;
        const bufferAfter = selectedService.buffer_after_minutes || 0;
        const currentMs = currentTime.getTime();
        slotEndMs = currentMs + (duration + bufferAfter) * 60_000;
        const slotStartWithBufferMs = currentMs - bufferBefore * 60_000;
        
        // Check overlap
        const overlaps = slotStartWithBufferMs < blocked.endMs && slotEndMs > blocked.startMs;
        if (overlaps) {
          hasConflict = true;
          // Next available time is when the blocking period ends
          nextAvailableTimeMs = blocked.endMs;
          break;
        }
      } else {
        // No service selected - use minimum duration
        const currentMs = currentTime.getTime();
        slotEndMs = currentMs + 30 * 60_000;
        const overlaps = currentMs < blocked.endMs && slotEndMs > blocked.startMs;
        if (overlaps) {
          hasConflict = true;
          nextAvailableTimeMs = blocked.endMs;
          break;
        }
      }
    }
    
    if (hasConflict && nextAvailableTimeMs != null) {
      // Jump to next available time (when blocking appointment ends)
      // This allows slots to start exactly when appointments end (e.g., 10:40)
      // Then continue with 30-minute intervals from there (10:40, 11:10, 11:40, etc.)
      const prevTime = currentTime;
      currentTime = new Date(nextAvailableTimeMs);

      // If the selected service has a buffer_before, we need the slot's buffer window to start
      // AFTER the blocked period ends. That means the earliest valid start is (blocked_end + buffer_before).
      if (selectedService) {
        const bufferBefore = selectedService.buffer_before_minutes || 0;
        if (bufferBefore > 0) {
          currentTime = addMinutes(currentTime, bufferBefore);
        }
      }

      // Safety: ensure forward progress to avoid infinite loops (e.g. when nextAvailableTime === currentTime)
      if (currentTime <= prevTime) {
        currentTime = addMinutes(prevTime, intervalMinutes);
      }
      // Ensure we don't go past business close time
      if (currentTime >= dayEnd) {
        break;
      }
      continue;
    }
    
    // No conflict - add this slot (only if before business close)
    if (currentTime < dayEnd) {
      const hours = currentTime.getHours();
      const minutes = currentTime.getMinutes();
      const timeStr = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      slots.push(timeStr);
    }
    
    // Move to next interval
    currentTime = addMinutes(currentTime, intervalMinutes);
  }
  
  return slots;
};

const formatIcsDateUtc = (date: Date): string => {
  // YYYYMMDDTHHMMSSZ
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(
    date.getUTCHours(),
  )}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
};

const buildGoogleCalendarUrl = (args: {
  title: string;
  startUtc: Date;
  endUtc: Date;
  details?: string;
  location?: string;
}): string => {
  const dates = `${formatIcsDateUtc(args.startUtc)}/${formatIcsDateUtc(args.endUtc)}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: args.title,
    dates,
  });
  if (args.details) params.set("details", args.details);
  if (args.location) params.set("location", args.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const downloadIcs = (args: {
  filename: string;
  title: string;
  description?: string;
  location?: string;
  startUtc: Date;
  endUtc: Date;
  uid: string;
}): void => {
  const dtstamp = formatIcsDateUtc(new Date());
  const dtstart = formatIcsDateUtc(args.startUtc);
  const dtend = formatIcsDateUtc(args.endUtc);

  const esc = (s: string) =>
    s
      .replace(/\\/g, "\\\\")
      .replace(/\n/g, "\\n")
      .replace(/,/g, "\\,")
      .replace(/;/g, "\\;");

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Buukd//Booking Suite//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${esc(args.uid)}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${dtstart}`,
    `DTEND:${dtend}`,
    `SUMMARY:${esc(args.title)}`,
    args.description ? `DESCRIPTION:${esc(args.description)}` : null,
    args.location ? `LOCATION:${esc(args.location)}` : null,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean) as string[];

  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = args.filename.endsWith(".ics") ? args.filename : `${args.filename}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const generateConfirmationCode = (): string => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // avoid ambiguous 0/O, 1/I
  let result = "";
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Helper function to get media items from a service
const getServiceMediaItems = (service: Service): MediaItem[] => {
  // Try to get from media_items column first
  const mediaItems = (service as any)?.media_items;
  if (Array.isArray(mediaItems) && mediaItems.length > 0) {
    return mediaItems.map((item: any) => ({
      url: item.url,
      type: item.type || (() => {
        const url = (item.url || '').toLowerCase();
        return url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov') || url.endsWith('.avi') || url.endsWith('.mkv')
          ? 'video' : 'image';
      })(),
    }));
  }
  
  // Fallback to image_url for backward compatibility
  if (service.image_url) {
    const url = service.image_url.toLowerCase();
    const isVideo = url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov') || url.endsWith('.avi') || url.endsWith('.mkv');
    return [{
      url: service.image_url,
      type: isVideo ? 'video' : 'image',
    }];
  }
  
  return [];
};

// First image URL for a product (for thumbnails; avoids video to prevent React DOM issues)
const getProductImageUrl = (product: any): string | null => {
  if (product?.image_url && typeof product.image_url === "string") return product.image_url;
  const items = product?.media_items;
  if (!Array.isArray(items) || items.length === 0) return null;
  const firstImage = items.find((i: any) => (i?.type || "image") === "image");
  if (firstImage?.url) return firstImage.url;
  const first = items[0];
  const url = first?.url;
  if (!url || typeof url !== "string") return null;
  const lower = url.toLowerCase();
  const isVideo = lower.endsWith(".mp4") || lower.endsWith(".webm") || lower.endsWith(".mov") || lower.endsWith(".avi") || lower.endsWith(".mkv");
  return isVideo ? null : url;
};

const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReducedMotion(!!mq.matches);
    onChange();
    // Safari fallback
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    // eslint-disable-next-line deprecation/deprecation
    mq.addListener(onChange);
    // eslint-disable-next-line deprecation/deprecation
    return () => mq.removeListener(onChange);
  }, []);
  return prefersReducedMotion;
};

export default function BookingPage() {
  const { businessSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectHandled = useRef(false);
  const { toast } = useToast();
  const [step, setStep] = useState<BookingStep>("service");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);
  const [createdAppointmentId, setCreatedAppointmentId] = useState<string | null>(null);
  const [createdAppointmentGroupId, setCreatedAppointmentGroupId] = useState<string | null>(null);
  const [startingDepositPayment, setStartingDepositPayment] = useState(false);

  const copyToClipboard = async (raw: string, successTitle = "Copied") => {
    const text = String(raw || "").trim();
    if (!text) return;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else if (typeof document !== "undefined") {
        const el = document.createElement("textarea");
        el.value = text;
        el.setAttribute("readonly", "true");
        el.style.position = "fixed";
        el.style.top = "0";
        el.style.left = "0";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      } else {
        throw new Error("Clipboard unavailable");
      }

      toast({ title: successTitle, description: "Copied to clipboard." });
    } catch {
      toast({
        title: "Copy failed",
        description: "Please copy manually.",
        variant: "destructive",
      });
    }
  };
  
  // Data from database
  const [business, setBusiness] = useState<Business | null>(null);
  const [extraSocialLinks, setExtraSocialLinks] = useState<any[]>([]);
  const [extraLogos, setExtraLogos] = useState<any[]>([]);

  const directDepositInstructions = String(
    (business as any)?.booking_deposit_payment_instructions || "",
  ).trim();
  const directDepositPhone = (() => {
    if (!directDepositInstructions) return "";
    const m = directDepositInstructions.match(/(\+?\d[\d\s-]{7,}\d)/);
    const raw = String(m?.[1] || "").trim();
    if (!raw) return "";
    return raw.replace(/[^\d+]/g, "");
  })();
  const directDepositName = (() => {
    if (!directDepositInstructions) return "";
    const m = directDepositInstructions.match(/name\s*:\s*([^\)\n\r]+)/i);
    return String(m?.[1] || "").trim();
  })();
  const [services, setServices] = useState<Service[]>([]);
  const [serviceHours, setServiceHours] = useState<ServiceHour[]>([]);
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [businessHours, setBusinessHours] = useState<any[]>([]);
  const [breakTimes, setBreakTimes] = useState<any[]>([]);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [defaultLocation, setDefaultLocation] = useState<Location | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [serviceLocations, setServiceLocations] = useState<Record<string, string[]>>({});
const [staffLocations, setStaffLocations] = useState<Record<string, string[]>>({});
  const selectedLocation = useMemo(() => {
    if (selectedLocationId) {
      const match = locations.find((location) => location.id === selectedLocationId);
      if (match) return match;
    }
    return defaultLocation || locations[0] || null;
  }, [defaultLocation, locations, selectedLocationId]);
const [serviceStaffMap, setServiceStaffMap] = useState<Record<string, string[]>>({});
  const [addonsByServiceId, setAddonsByServiceId] = useState<Record<string, ServiceAddon[]>>({});
  const [addonsLoadingByServiceId, setAddonsLoadingByServiceId] = useState<Record<string, boolean>>({});
  const [existingAppointments, setExistingAppointments] = useState<any[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [multiSlotsLoading, setMultiSlotsLoading] = useState(false);
  
  // Selection state
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedServicesSummaryOpen, setSelectedServicesSummaryOpen] = useState(false);
  
  // Gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryMedia, setGalleryMedia] = useState<MediaItem[]>([]);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [selectedServiceCategory, setSelectedServiceCategory] = useState<"all" | "uncategorized" | "shop" | string>("all");
  const [products, setProducts] = useState<any[]>([]);
  const [productCart, setProductCart] = useState<Record<string, number>>({});
  const [productFulfillmentType, setProductFulfillmentType] = useState<"pickup" | "delivery">("pickup");
  const [productDeliveryAddress, setProductDeliveryAddress] = useState("");
  const [productDeliveryPlaceId, setProductDeliveryPlaceId] = useState("");
  const [productDeliveryLat, setProductDeliveryLat] = useState<number | null>(null);
  const [productDeliveryLng, setProductDeliveryLng] = useState<number | null>(null);
  // Add-ons are priced per unit; store quantities per service -> add-on id (0 = not selected)
  const [addonQtyByServiceId, setAddonQtyByServiceId] = useState<Record<string, Record<string, number>>>({});
  const [selectedStaff, setSelectedStaff] = useState<StaffOption | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [customerDetails, setCustomerDetails] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const defaultPhoneCountry: Country = "GH";
  const normalizedCustomerPhone = useMemo(
    () => normalizeBookingPhone(customerDetails.phone),
    [customerDetails.phone]
  );
  const phoneValidationError = useMemo(() => {
    if (!phoneTouched && !customerDetails.phone.trim()) return null;
    return getBookingPhoneValidationError(customerDetails.phone);
  }, [customerDetails.phone, phoneTouched]);

  // Inspiration media (optional): images/videos attached at booking
  const [inspirationFiles, setInspirationFiles] = useState<
    { id: string; file: File; objectUrl?: string }[]
  >([]);
  const inspirationInputRef = useRef<HTMLInputElement>(null);

  // Coupons (optional)
  const [couponCode, setCouponCode] = useState("");
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<null | {
    id: string;
    code: string;
    discount_type: "percent" | "amount";
    discount_value: number;
  }>(null);

  // Generate next 14 days for date selection
  const today = startOfDay(new Date());
  const dates = Array.from({ length: 14 }, (_, i) => addDays(today, i));
  const prefersReducedMotion = usePrefersReducedMotion();

  const normalizeMediaItems = (raw: any, opts?: { cap?: number; dedupe?: boolean }): MediaItem[] => {
    const cap = Math.max(1, Number(opts?.cap ?? 6));
    const dedupe = opts?.dedupe !== false;
    const seen = new Set<string>();
    const out: MediaItem[] = [];

    if (!Array.isArray(raw)) return out;

    for (const it of raw) {
      const url = String(it?.url || "").trim();
      if (!url) continue;

      const tRaw = String(it?.type || "").toLowerCase();
      const type: MediaItem["type"] =
        tRaw === "video" || tRaw === "image"
          ? (tRaw as any)
          : (() => {
              const lower = url.toLowerCase();
              return lower.endsWith(".mp4") ||
                lower.endsWith(".webm") ||
                lower.endsWith(".mov") ||
                lower.endsWith(".avi") ||
                lower.endsWith(".mkv")
                ? "video"
                : "image";
            })();

      const key = `${type}:${url}`;
      if (dedupe && seen.has(key)) continue;
      seen.add(key);

      out.push({ url, type });
      if (out.length >= cap) break;
    }

    return out;
  };

  const bookingHeaderMedia = (business as any)?.booking_header_media_items;
  const heroMediaItems = useMemo(() => {
    // Prefer explicit booking header media (business-level customization)
    if (Array.isArray(bookingHeaderMedia) && bookingHeaderMedia.length > 0) {
      // IMPORTANT: respect the business-configured order (do not drop videos).
      return normalizeMediaItems(bookingHeaderMedia, { cap: 6, dedupe: true });
    }

    const items: MediaItem[] = [];
    const seen = new Set<string>();

    // Pull from service media (best available today). We cap to keep the carousel light.
    for (const s of services || []) {
      const media = getServiceMediaItems(s);
      for (const m of media) {
        const url = String(m?.url || "").trim();
        const type = String(m?.type || "image") as MediaItem["type"];
        if (!url) continue;
        const key = `${type}:${url}`;
        if (seen.has(key)) continue;
        seen.add(key);
        items.push({ url, type });
        if (items.length >= 6) break;
      }
      if (items.length >= 6) break;
    }

    // Prefer images for the hero background (videos are heavier). If none, fall back to videos.
    const images = items.filter((i) => i.type === "image");
    const videos = items.filter((i) => i.type === "video");
    return images.length > 0 ? images : videos;
  }, [services, business?.id, bookingHeaderMedia]);

  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    setHeroIndex(0);
  }, [business?.id]);

  const heroCurrent = heroMediaItems[heroIndex] || null;
  const heroHasMultiple = heroMediaItems.length > 1;

  useEffect(() => {
    if (step !== "service") return;
    if (prefersReducedMotion) return;
    if (!heroHasMultiple) return;
    if (heroCurrent?.type === "video") return; // advance videos via onEnded

    const id = window.setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroMediaItems.length);
    }, 5000);
    return () => window.clearInterval(id);
  }, [step, prefersReducedMotion, heroHasMultiple, heroCurrent?.type, heroMediaItems.length]);

  // Fetch business, services, and staff
  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!businessSlug) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch business by slug
        const { data: businessData, error: businessError } = await supabase
          .from("businesses")
          .select("*")
          .eq("slug", businessSlug)
          .single();

        if (businessError) throw businessError;
        if (!businessData) throw new Error("Business not found");

        setBusiness(businessData);

        // Fetch services for this business
        const { data: servicesData, error: servicesError } = await supabase
          .from("services")
          .select("*")
          .eq("business_id", businessData.id)
          .eq("active", true)
          .order("name");

        if (servicesError) throw servicesError;
        setServices(servicesData || []);

        const { data: serviceHoursData, error: serviceHoursError } = await supabase
          .from("service_hours")
          .select("*")
          .eq("business_id", businessData.id)
          .order("day_of_week");
        if (serviceHoursError) {
          console.error("Error fetching service hours:", serviceHoursError);
          setServiceHours([]);
        } else {
          setServiceHours(serviceHoursData || []);
        }

        // Fetch service categories (for category filter chips)
        const { data: catsData, error: catsError } = await supabase
          .from("service_categories")
          .select("*")
          .eq("business_id", businessData.id)
          .eq("active", true)
          .order("position", { ascending: true })
          .order("name", { ascending: true });
        if (catsError) {
          setServiceCategories([]);
        } else {
          setServiceCategories((catsData || []) as any);
        }

        // Fetch staff for this business
        const { data: staffData, error: staffError } = await supabase
          .from("staff")
          .select("*")
          .eq("business_id", businessData.id)
          .eq("active", true)
          .order("name");

        if (staffError) throw staffError;
        setStaff(staffData || []);

        // Business hours and break times will be fetched when location is selected

        // Fetch all locations for this business
        const { data: allLocations, error: locationsError } = await supabase
          .from("locations")
          .select("*")
          .eq("business_id", businessData.id)
          .order("is_default", { ascending: false })
          .order("created_at", { ascending: true });

        if (locationsError) {
          console.error("Error fetching locations:", locationsError);
          setLocations([]);
        } else {
          setLocations(allLocations || []);
          // Set default location (prefer businesses.location_id, fallback to is_default)
          const defaultLoc = (businessData as any)?.location_id
            ? (allLocations || []).find((l) => l.id === (businessData as any).location_id)
            : (allLocations || []).find((l) => (l as any).is_default) || (allLocations || [])[0];
          setDefaultLocation(defaultLoc || null);
          // Set selected location to default if only one location, or first location
          if (allLocations && allLocations.length > 0) {
            setSelectedLocationId(defaultLoc?.id || allLocations[0].id);
          }
        }

        // Fetch service-location assignments (for services we fetched)
        const serviceIds = (servicesData || []).map((s) => s.id);
        let serviceLocMap: Record<string, string[]> = {};

        if (serviceIds.length > 0) {
          const { data: serviceLocData, error: serviceLocError } = await supabase
            .from("service_locations" as any)
            .select("service_id, location_id")
            .in("service_id", serviceIds);

          if (serviceLocError) {
            console.error("Error fetching service locations:", serviceLocError);
          } else if (serviceLocData) {
            serviceLocData.forEach((sl: any) => {
              if (!serviceLocMap[sl.service_id]) {
                serviceLocMap[sl.service_id] = [];
              }
              serviceLocMap[sl.service_id].push(sl.location_id);
            });
          }
        }

        setServiceLocations(serviceLocMap);

        // Fetch staff-location assignments (for staff we fetched)
        const staffIds = (staffData || []).map((s) => s.id);
        let staffLocMap: Record<string, string[]> = {};

        if (staffIds.length > 0) {
          const { data: staffLocData, error: staffLocError } = await supabase
            .from("staff_locations" as any)
            .select("staff_id, location_id")
            .in("staff_id", staffIds);

          if (staffLocError) {
            console.error("Error fetching staff locations:", staffLocError);
          } else if (staffLocData) {
            staffLocData.forEach((sl: any) => {
              if (!staffLocMap[sl.staff_id]) {
                staffLocMap[sl.staff_id] = [];
              }
              staffLocMap[sl.staff_id].push(sl.location_id);
            });
          }
        }

        setStaffLocations(staffLocMap);

        // Fetch staff-service assignments to restrict staff per service
        let serviceStaff: Record<string, string[]> = {};
        if (serviceIds.length > 0 && staffIds.length > 0) {
          const { data: staffServiceData, error: staffServiceError } = await supabase
            .from("staff_services" as any)
            .select("service_id, staff_id")
            .in("service_id", serviceIds)
            .in("staff_id", staffIds);

          if (staffServiceError) {
            console.error("Error fetching staff-services:", staffServiceError);
          } else if (staffServiceData) {
            (staffServiceData as any[]).forEach((row) => {
              if (!serviceStaff[row.service_id]) {
                serviceStaff[row.service_id] = [];
              }
              serviceStaff[row.service_id].push(row.staff_id);
            });
          }
        }

        setServiceStaffMap(serviceStaff);

        // Fetch products when sales enabled
        if ((businessData as any)?.sales_enabled) {
          const { data: productsData, error: productsError } = await (supabase as any)
            .from("business_products")
            .select("*")
            .eq("business_id", businessData.id)
            .eq("active", true)
            .order("position", { ascending: true })
            .order("name");
          if (!productsError) setProducts(productsData || []);
          else {
            console.error("Error fetching shop products:", productsError);
            setProducts([]);
          }
        } else {
          setProducts([]);
        }

      } catch (err) {
        console.error("Error fetching business data:", err);
        setError(err instanceof Error ? err.message : "Failed to load booking page");
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [businessSlug]);

  // Load additional branding (extra socials + extra logos)
  useEffect(() => {
    const businessId = business?.id ? String(business.id) : "";
    if (!businessId) return;
    let cancelled = false;
    (async () => {
      try {
        const [linksRes, logosRes] = await Promise.all([
          supabase
            .from("business_social_links" as any)
            .select("id, platform, label, url, position, active, is_primary")
            .eq("business_id", businessId)
            .eq("active", true)
            .order("platform", { ascending: true })
            .order("is_primary", { ascending: false })
            .order("position", { ascending: true }),
          supabase
            .from("business_logos" as any)
            .select("id, label, url, position, active")
            .eq("business_id", businessId)
            .eq("active", true)
            .order("position", { ascending: true }),
        ]);
        if (cancelled) return;
        setExtraSocialLinks((linksRes as any)?.data || []);
        setExtraLogos((logosRes as any)?.data || []);
      } catch {
        if (cancelled) return;
        setExtraSocialLinks([]);
        setExtraLogos([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [business?.id]);

  const extraAccountsFor = (platform: string) => {
    const key = String(platform || "").trim().toLowerCase();
    const rows = (extraSocialLinks || []).filter(
      (r: any) => String(r.platform || "").trim().toLowerCase() === key,
    );
    return rows
      .filter((r: any) => !r?.is_primary)
      .map((r: any) => ({ url: String(r.url || ""), label: String(r.label || "").trim() || "Other" }));
  };

  const primaryLabelFor = (platform: string) => {
    const key = String(platform || "").trim().toLowerCase();
    const row = (extraSocialLinks || []).find(
      (r: any) => String(r.platform || "").trim().toLowerCase() === key && !!r?.is_primary,
    );
    return String(row?.label || "Main").trim() || "Main";
  };

  // Fetch business hours and break times when location changes
  useEffect(() => {
    const fetchLocationHours = async () => {
      if (!business) {
        // Fallback to default hours if no business
        const slots = generateTimeSlots("09:00", "17:00", 30);
        setTimeSlots(slots);
        setBusinessHours([]);
        setBreakTimes([]);
        return;
      }

      try {
        // Production-ready approach: Fetch hours using two queries and combine
        // This is more reliable than .or() with null checks
        // Always fetch global hours (location_id = null)
        const globalQuery = supabase
          .from("business_hours")
          .select("*")
          .eq("business_id", business.id)
          .is("location_id", null)
          .order("day_of_week");
        
        // If location is selected, also fetch location-specific hours
        const locationQuery = selectedLocationId
          ? supabase
              .from("business_hours")
              .select("*")
              .eq("business_id", business.id)
              .eq("location_id", selectedLocationId)
              .order("day_of_week")
          : null;
        
        // Execute queries in parallel
        const [globalResult, locationResult] = await Promise.all([
          globalQuery,
          locationQuery || Promise.resolve({ data: [], error: null })
        ]);
        
        // Combine results, prioritizing location-specific hours over global
        const globalHours = globalResult.data || [];
        const locationHours = locationResult?.data || [];
        
        // Merge: use location-specific hours if they exist, otherwise use global
        const hoursMap = new Map<number, any>();
        
        // First, add global hours
        globalHours.forEach((h: any) => {
          hoursMap.set(h.day_of_week, h);
        });
        
        // Then, override with location-specific hours if they exist
        locationHours.forEach((h: any) => {
          hoursMap.set(h.day_of_week, h);
        });
        
        const hoursData = Array.from(hoursMap.values());
        const hoursError = globalResult.error || locationResult?.error || null;

        if (hoursError) {
          console.error("Error fetching business hours:", hoursError);
          const slots = generateTimeSlots("09:00", "17:00", 30);
          setTimeSlots(slots);
          setBusinessHours([]);
        } else {
          const hoursCount = hoursData?.length || 0;
          
          // If no hours found, create default hours (9 AM - 5 PM, Mon-Fri)
          if (hoursCount === 0) {
            const defaultHours = [
              { day_of_week: 1, open_time: "09:00", close_time: "17:00", is_closed: false, location_id: selectedLocationId },
              { day_of_week: 2, open_time: "09:00", close_time: "17:00", is_closed: false, location_id: selectedLocationId },
              { day_of_week: 3, open_time: "09:00", close_time: "17:00", is_closed: false, location_id: selectedLocationId },
              { day_of_week: 4, open_time: "09:00", close_time: "17:00", is_closed: false, location_id: selectedLocationId },
              { day_of_week: 5, open_time: "09:00", close_time: "17:00", is_closed: false, location_id: selectedLocationId },
            ];
            setBusinessHours(defaultHours);
          } else {
          setBusinessHours(hoursData || []);
          }
          
          // Generate default time slots (will be updated when date is selected)
          const slots = generateTimeSlots("09:00", "17:00", 30);
          setTimeSlots(slots);
        }

        // Fetch break times using the same reliable approach
        // Always fetch global breaks (location_id = null)
        const globalBreakQuery = supabase
          .from("break_times" as any)
          .select("*")
          .eq("business_id", business.id)
          .is("location_id", null)
          .order("day_of_week")
          .order("start_time");
        
        // If location is selected, also fetch location-specific breaks
        const locationBreakQuery = selectedLocationId
          ? supabase
              .from("break_times" as any)
              .select("*")
              .eq("business_id", business.id)
              .eq("location_id", selectedLocationId)
              .order("day_of_week")
              .order("start_time")
          : null;
        
        // Execute both queries in parallel
        const [globalBreakResult, locationBreakResult] = await Promise.all([
          globalBreakQuery,
          locationBreakQuery || Promise.resolve({ data: [], error: null })
        ]);
        
        // Combine break times (location-specific override global for same day/time)
        const globalBreaks = globalBreakResult.data || [];
        const locationBreaks = locationBreakResult?.data || [];
        
        // Use location-specific breaks if available, otherwise use global
        const breaksData = locationBreaks.length > 0 ? locationBreaks : globalBreaks;
        const breaksError = globalBreakResult.error || locationBreakResult?.error || null;

        if (breaksError) {
          console.error("Error fetching break times:", breaksError);
          setBreakTimes([]);
        } else {
          setBreakTimes((breaksData || []) as any[]);
        }
      } catch (err) {
        console.error("Error fetching location hours:", err);
      }
    };

    fetchLocationHours();
  }, [business, selectedLocationId]);

  const serviceCategoryPositionById = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of serviceCategories || []) {
      const id = String((c as any)?.id || "").trim();
      if (!id) continue;
      map.set(id, Number((c as any)?.position) || 9999);
    }
    return map;
  }, [serviceCategories]);

  // Category and location-filtered services for the service-selection step
  const visibleServices = useMemo(() => {
    let filtered = selectedServiceCategory === "all"
      ? services
      : selectedServiceCategory === "uncategorized"
        ? services.filter((s: any) => !s.category_id)
        : services.filter((s: any) => String((s as any).category_id) === String(selectedServiceCategory));

    // Filter by location: if service has no location assignments, it's available at all locations
    // Otherwise, check if it's assigned to the selected location
    if (selectedLocationId) {
      filtered = filtered.filter((service) => {
        const serviceLocationIds = serviceLocations[service.id];
        // If service has no location assignments (undefined or empty array), it's available at all locations
        if (!serviceLocationIds || serviceLocationIds.length === 0) {
          return true;
        }
        // Otherwise, check if it's assigned to the selected location
        return serviceLocationIds.includes(selectedLocationId);
      });
    }

    return [...filtered].sort((a, b) => {
      const catA = String((a as any)?.category_id || "");
      const catB = String((b as any)?.category_id || "");
      const posA = catA ? (serviceCategoryPositionById.get(catA) ?? 9999) : 9998;
      const posB = catB ? (serviceCategoryPositionById.get(catB) ?? 9999) : 9998;
      if (posA !== posB) return posA - posB;
      return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
    });
  }, [services, selectedServiceCategory, selectedLocationId, serviceLocations, serviceCategoryPositionById]);

  // Categories that allow selecting multiple services in the same category (e.g. "Extras").
  // Long-term: controlled by `service_categories.allow_multiple_services`.
  // Backward compatibility: if the column isn't present yet, fall back to a name heuristic.
  const multiSelectCategoryIds = useMemo(() => {
    const set = new Set<string>();
    for (const c of serviceCategories || []) {
      const id = String((c as any)?.id || "").trim();
      if (!id) continue;

      const explicit = (c as any)?.allow_multiple_services === true;
      if (explicit) {
        set.add(id);
        continue;
      }

      // Fallback heuristic (safe no-op once the flag is configured)
      const name = String((c as any)?.name || "").trim().toLowerCase();
      if (
        /\bextra(s)?\b/i.test(name) ||
        /\badd[-\s]?on(s)?\b/i.test(name) ||
        /\btopping(s)?\b/i.test(name)
      ) {
        set.add(id);
      }
    }
    return set;
  }, [serviceCategories]);

  const multiServiceEnabled = (business as any)?.multi_service_booking_enabled === true;
  const onlineCheckoutEnabled = businessUsesOnlineCheckout(business as any);
  const collectDepositsViaBuukd = onlineCheckoutEnabled;
  const allowFullPaymentAtBooking = (business as any)?.allow_full_payment_at_booking === true;
  const onlinePaymentsAvailable = useMemo(
    () => businessAllowsCustomerPayments(business as any),
    [business],
  );

  // Deep link from Discover: /book/:slug?service=:id
  useEffect(() => {
    if (loading || !business || preselectHandled.current) return;

    const serviceId = searchParams.get("service")?.trim();
    if (!serviceId) return;

    const service = services.find((s) => s.id === serviceId);
    if (!service) return;

    preselectHandled.current = true;
    setStep("service");
    setSelectedServiceCategory("all");

    if (multiServiceEnabled) {
      setSelectedServiceIds([service.id]);
      setSelectedService(service);
    } else {
      setSelectedService(service);
      setSelectedServiceIds([]);
    }

    requestAnimationFrame(() => {
      document
        .getElementById(`booking-service-${service.id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [loading, business, services, searchParams, multiServiceEnabled]);

  const selectedServices = useMemo(() => {
    if (!multiServiceEnabled) return selectedService ? [selectedService] : [];
    const byId = new Map(services.map((s) => [s.id, s] as const));
    return selectedServiceIds.map((id) => byId.get(id)).filter(Boolean) as Service[];
  }, [multiServiceEnabled, selectedService?.id, selectedServiceIds, services]);

  const serviceHoursByServiceId = useMemo(() => {
    const map = new Map<string, ServiceHour[]>();
    for (const hour of serviceHours) {
      const rows = map.get(hour.service_id) || [];
      rows.push(hour);
      map.set(hour.service_id, rows);
    }
    return map;
  }, [serviceHours]);

  const selectedServiceScheduleItems = useMemo(
    () => selectedServices.map((service) => ({
      id: service.id,
      duration: Math.max(1, Number(service.duration_minutes) || 30),
    })),
    [selectedServices],
  );

  const servicesAvailableOnWeekday = (dayOfWeek: number) =>
    areServicesAvailableOnDay(selectedServiceScheduleItems, serviceHours, dayOfWeek);

  const slotFitsSelectedServiceHours = (slot: string, dayOfWeek: number) =>
    doesSlotFitServiceHours(slot, selectedServiceScheduleItems, serviceHours, dayOfWeek);

  useEffect(() => {
    if (!selectedDate || selectedServices.length === 0) return;
    if (!servicesAvailableOnWeekday(selectedDate.getDay())) {
      setSelectedDate(null);
      setSelectedTime(null);
      setTimeSlots([]);
    }
  }, [selectedDate, selectedServices, selectedServiceScheduleItems, serviceHours]);

  const selectedServicesBarTitle = useMemo(() => {
    if (selectedServices.length === 0) return "";
    if (selectedServices.length === 1) return selectedServices[0].name;
    const names = selectedServices.map((s) => s.name).filter(Boolean);
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [selectedServices]);

  const focusedServiceId = selectedService?.id || null;
  const focusedAddons = focusedServiceId ? addonsByServiceId[focusedServiceId] || [] : [];
  const focusedAddonQtyById = focusedServiceId ? addonQtyByServiceId[focusedServiceId] || {} : {};

  // Fetch add-ons for focused service (lazy, cached per service)
  useEffect(() => {
    const fetchAddons = async () => {
      if (!selectedService) return;
      const sid = selectedService.id;
      if (addonsByServiceId[sid]) return;
      try {
        setAddonsLoadingByServiceId((prev) => ({ ...prev, [sid]: true }));
        const { data, error } = await supabase
          .from("service_addons")
          .select("*")
          .eq("service_id", sid)
          .eq("active", true)
          .order("created_at", { ascending: true });
        if (error) throw error;
        setAddonsByServiceId((prev) => ({ ...prev, [sid]: (data || []) as any }));
      } catch (e) {
        setAddonsByServiceId((prev) => ({ ...prev, [sid]: [] as any }));
      } finally {
        setAddonsLoadingByServiceId((prev) => ({ ...prev, [sid]: false }));
      }
    };
    fetchAddons();
  }, [selectedService?.id, addonsByServiceId]);

  const selectedAddons = focusedAddons.filter((a) => (focusedAddonQtyById[a.id] || 0) > 0);
  const addonsTotal = selectedAddons.reduce((sum, a) => {
    const qty = focusedAddonQtyById[a.id] || 0;
    return sum + (Number(a.price_amount) || 0) * qty;
  }, 0);

  const serviceBase = selectedService ? Number(selectedService.price_amount) || 0 : 0;
  const servicesBaseTotal = selectedServices.reduce((sum, s) => sum + (Number(s.price_amount) || 0), 0);
  const addonsTotalAll = useMemo(() => {
    return selectedServices.reduce((sum, s) => {
      const addons = addonsByServiceId[s.id] || [];
      const qtyMap = addonQtyByServiceId[s.id] || {};
      const t = addons.reduce((acc, a) => acc + (Number(a.price_amount) || 0) * (qtyMap[a.id] || 0), 0);
      return sum + t;
    }, 0);
  }, [selectedServices, addonsByServiceId, addonQtyByServiceId]);

  const totalPrice = servicesBaseTotal + addonsTotalAll;
  const totalDurationMinutes = selectedServices.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0);

  // When a service is free (price 0) and has add-ons, require at least one add-on selected before continuing
  const freeServicesMissingAddons = useMemo(() => {
    return selectedServices.filter((s) => {
      const price = Number(s.price_amount) || 0;
      if (price !== 0) return false;
      const addons = addonsByServiceId[s.id] || [];
      if (addons.length === 0) return false;
      const qtyMap = addonQtyByServiceId[s.id] || {};
      const hasAny = addons.some((a) => (qtyMap[a.id] || 0) > 0);
      return !hasAny;
    });
  }, [selectedServices, addonsByServiceId, addonQtyByServiceId]);
  const mustSelectAddons = freeServicesMissingAddons.length > 0;

  const depositTotal = useMemo(() => {
    return selectedServices.reduce((sum, s) => {
      const req = !!(s as any)?.requires_deposit;
      const amt = Number((s as any)?.deposit_amount || 0);
      return sum + (req ? Math.max(0, amt) : 0);
    }, 0);
  }, [selectedServices]);

  const requiresDeposit =
    (multiServiceEnabled ? depositTotal > 0 : !!(selectedService as any)?.requires_deposit) && depositTotal > 0;
  const depositAmount = multiServiceEnabled ? depositTotal : Number((selectedService as any)?.deposit_amount || 0);
  const requiresReservationFee =
    !requiresDeposit && onlineCheckoutEnabled && onlinePaymentsAvailable;
  const reservationPayTotal = customerPaystackChargeAmount(0);
  const requiresEmailForOnlinePayment =
    (requiresDeposit || requiresReservationFee) && collectDepositsViaBuukd;
  const normalizedCustomerEmail = useMemo(
    () => normalizeOptionalEmail(customerDetails.email),
    [customerDetails.email]
  );
  const emailValidationError = useMemo(
    () => getBookingEmailValidationError(customerDetails.email, requiresEmailForOnlinePayment),
    [customerDetails.email, requiresEmailForOnlinePayment]
  );
  const shouldShowEmailValidationError =
    !!emailValidationError && (emailTouched || !!customerDetails.email.trim() || requiresEmailForOnlinePayment);

  const couponFloor = requiresDeposit ? Math.max(0, depositAmount) : 0;
  const rawDiscount = (() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.discount_type === "percent") {
      return (totalPrice * Number(appliedCoupon.discount_value || 0)) / 100;
    }
    return Number(appliedCoupon.discount_value || 0);
  })();
  const discountCap = Math.max(0, totalPrice - couponFloor);
  const discountAmount = Math.min(Math.max(0, rawDiscount), discountCap);
  const totalAfterDiscount = Math.max(0, totalPrice - discountAmount);
  const paystackCustomerTotal = (amount: number) =>
    collectDepositsViaBuukd ? customerPaystackChargeAmount(amount) : amount;
  const platformBookingFee = collectDepositsViaBuukd ? customerPlatformBookingFeeAmount() : 0;
  const depositPayTotal = paystackCustomerTotal(Number(depositAmount || 0));
  const fullPayTotal = paystackCustomerTotal(totalAfterDiscount);

  const productCartTotal = useMemo(() => {
    return Object.entries(productCart).reduce((sum, [productId, qty]) => {
      if (!qty || qty <= 0) return sum;
      const p = products.find((x) => x.id === productId);
      if (!p) return sum;
      return sum + (Number(p.price_amount) || 0) * qty;
    }, 0);
  }, [productCart, products]);

  const productCartCurrencies = useMemo(() => {
    return Array.from(
      new Set(
        Object.entries(productCart)
          .filter(([, qty]) => qty > 0)
          .map(([productId]) => {
            const product = products.find((x) => x.id === productId);
            return String(product?.currency || business?.currency || "USD");
          }),
      ),
    );
  }, [business?.currency, productCart, products]);

  // Fetch existing appointments for selected date
  useEffect(() => {
    const fetchAppointments = async () => {
      if (multiServiceEnabled) {
        setExistingAppointments([]);
        setAppointmentsLoading(false);
        return;
      }
      if (!selectedDate || !business?.id) {
        setExistingAppointments([]);
        setAppointmentsLoading(false);
        return;
      }

      try {
        setAppointmentsLoading(true);
        const dayStart = new Date(selectedDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(selectedDate);
        dayEnd.setHours(23, 59, 59, 999);

        const now = new Date();
        let query = supabase
          .from("appointments")
          .select(`
            id, 
            start_datetime, 
            end_datetime, 
            service_id, 
            staff_id, 
            status,
            location_id,
            services (
              duration_minutes,
              buffer_before_minutes,
              buffer_after_minutes
            )
          `)
          .eq("business_id", business.id)
          .gte("start_datetime", dayStart.toISOString())
          .lte("start_datetime", dayEnd.toISOString())
          .in("status", ["pending", "confirmed", "rescheduled"]); 
        
        // Filter by location if one is selected
        // Appointments at one location shouldn't block slots at another location
        if (selectedLocationId) {
          query = query.or(`location_id.eq.${selectedLocationId},location_id.is.null`);
        }
        
        // Only filter by end_datetime if we're looking at today or future dates
        // For past dates, we don't need this filter
        const todayStart = startOfDay(now);
        const selectedDateStart = startOfDay(selectedDate);
        if (!isBefore(selectedDateStart, todayStart)) {
          query = query.gte("end_datetime", now.toISOString());
        }
        
        const { data, error } = await query;
          // Only check active appointments - cancelled, completed, and no_show don't block slots
          // This means slots become available again when appointments are:
          // - Cancelled (customer or business cancels)
          // - Completed (past appointments don't block future bookings)
          // - Marked as no_show (slot becomes available for rebooking)
          // - Past appointments (end_datetime < now) don't block slots

        if (error) {
          console.error("Error fetching appointments:", error);
          setExistingAppointments([]);
        } else {
          setExistingAppointments(data || []);
        }
      } catch (err) {
        console.error("Error fetching appointments:", err);
        setExistingAppointments([]);
      } finally {
        setAppointmentsLoading(false);
      }
    };

    fetchAppointments();
  }, [selectedDate, business?.id, selectedLocationId, multiServiceEnabled]);

  const couponScopeKey = useMemo(() => {
    if (!multiServiceEnabled) return selectedService?.id || "";
    // Service/category scoped coupons must be revalidated when the selected bundle changes.
    return selectedServiceIds.slice().sort().join(",");
  }, [multiServiceEnabled, selectedService?.id, selectedServiceIds]);

  // If the selected service/bundle changes, clear any applied coupon (service/category scoped coupons may not apply)
  useEffect(() => {
    setAppliedCoupon(null);
    setCouponError(null);
    // keep couponCode so user can re-apply quickly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponScopeKey]);

  const handleSelectDate = (date: Date | undefined) => {
    const nextDate = date || null;
    setSelectedDate(nextDate);
    setSelectedTime(null);

    // Prevent the "flash" of unfiltered slots by immediately entering a loading state
    // and clearing any stale slots/appointments from the previous selection.
    if (nextDate && business?.id) {
      setAppointmentsLoading(true);
      setExistingAppointments([]);
      setTimeSlots([]);
    }
  };

  // Update time slots when date is selected, filtering out booked times
  useEffect(() => {
    if (multiServiceEnabled) return;
    if (!selectedDate) {
      setTimeSlots(generateTimeSlots("09:00", "17:00", 30));
      return;
    }

    // While appointments are loading for this date, keep slots empty so the UI shows skeleton.
    if (appointmentsLoading) {
      setTimeSlots([]);
      return;
    }

    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 6 = Saturday
    const dayHours = businessHours.find((h) => h.day_of_week === dayOfWeek) || (
      businessHours.length === 0
        ? { open_time: "09:00", close_time: "17:00", is_closed: false }
        : null
    );

    if (!dayHours || dayHours.is_closed) {
      // Day is closed, no time slots
      setTimeSlots([]);
      return;
    }

    let effectiveOpen = dayHours.open_time;
    let effectiveClose = dayHours.close_time;
    if (selectedService) {
      const customHours = serviceHoursByServiceId.get(selectedService.id);
      if (customHours?.length) {
        const serviceHour = customHours.find((hour) => hour.day_of_week === dayOfWeek);
        if (!serviceHour) {
          setTimeSlots([]);
          return;
        }
        effectiveOpen = minutesToTime(Math.max(timeToMinutes(effectiveOpen), timeToMinutes(serviceHour.start_time)));
        effectiveClose = minutesToTime(Math.min(timeToMinutes(effectiveClose), timeToMinutes(serviceHour.end_time)));
        if (timeToMinutes(effectiveOpen) >= timeToMinutes(effectiveClose)) {
          setTimeSlots([]);
          return;
        }
      }
    }

    // Use dynamic slot generation that accounts for buffer times
    const availableSlots = generateDynamicTimeSlots(
      effectiveOpen,
      effectiveClose,
      selectedDate,
      existingAppointments,
      breakTimes,
      selectedService,
      selectedStaff,
      selectedLocationId,
      30 // 30-minute intervals
    ).filter((slot) => slotFitsSelectedServiceHours(slot, dayOfWeek));

    setTimeSlots(availableSlots);
  }, [selectedDate, businessHours, breakTimes, existingAppointments, selectedService, selectedStaff, selectedLocationId, appointmentsLoading, multiServiceEnabled, serviceHoursByServiceId, selectedServiceScheduleItems]);

  // Multi-service availability: fetch slots server-side (provider-aware)
  useEffect(() => {
    const fetchMultiSlots = async () => {
      if (!multiServiceEnabled) return;
      if (!business?.id || !selectedDate) {
        setTimeSlots([]);
        return;
      }
      if (selectedServiceIds.length === 0) {
        setTimeSlots([]);
        return;
      }

      try {
        setMultiSlotsLoading(true);
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const { data, error } = await (supabase as any).rpc("get_multi_service_time_slots_public", {
          p_business_id: business.id,
          p_location_id: selectedLocationId || null,
          p_date: dateStr,
          p_service_ids: selectedServiceIds,
          p_interval_minutes: 30,
        });
        if (error) throw error;
        const slots = (data || [])
          .map((r: any) => String(r?.slot_time ?? r?.time ?? r))
          .filter(Boolean)
          .filter((slot: string) => slotFitsSelectedServiceHours(slot, selectedDate.getDay()));
        setTimeSlots(slots);
      } catch (e) {
        console.error("Failed to fetch multi-service slots:", e);
        setTimeSlots([]);
      } finally {
        setMultiSlotsLoading(false);
      }
    };

    void fetchMultiSlots();
  }, [multiServiceEnabled, business?.id, selectedDate, selectedLocationId, selectedServiceIds, selectedServices, serviceHoursByServiceId]);

  const goToStep = (newStep: BookingStep) => {
    setStep(newStep);
    window.scrollTo(0, 0);
    // Reset terms agreement when leaving details step
    if (newStep !== "details") {
      setAgreedToTerms(false);
    }
  };

  const applyCoupon = async () => {
    if (!business?.id) return;
    const code = couponCode.trim().toUpperCase();
    setCouponError(null);
    if (!code) {
      setAppliedCoupon(null);
      return;
    }

    setCouponApplying(true);
    try {
      const serviceIds = multiServiceEnabled ? selectedServiceIds : selectedService?.id ? [selectedService.id] : [];
      if (serviceIds.length === 0) {
        setAppliedCoupon(null);
        setCouponError("Select a service first");
        return;
      }

      let lastRow: any = null;
      let couponId: string | null = null;

      for (const sid of serviceIds) {
        const { data, error } = await (supabase as any).rpc("validate_business_coupon_public", {
          p_business_id: business.id,
          p_code: code,
          p_service_id: sid,
        });
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        if (!row?.ok) {
          setAppliedCoupon(null);
          setCouponError(String(row?.error || "Invalid coupon code"));
          return;
        }
        if (couponId && String(row.coupon_id) !== couponId) {
          setAppliedCoupon(null);
          setCouponError("Coupon validation mismatch. Please try again.");
          return;
        }
        couponId = String(row.coupon_id);
        lastRow = row;
      }

      if (!lastRow?.ok || !couponId) {
        setAppliedCoupon(null);
        setCouponError("Invalid coupon code");
        return;
      }

      setAppliedCoupon({
        id: couponId,
        code: String(lastRow.code || code),
        discount_type: lastRow.discount_type === "amount" ? "amount" : "percent",
        discount_value: Number(lastRow.discount_value || 0),
      });
      setCouponError(null);
    } catch (e: any) {
      console.error("Coupon validation failed:", e);
      setAppliedCoupon(null);
      const msg = String(e?.message || "Failed to apply coupon");
      setCouponError(msg.toLowerCase().includes("could not find") ? "Coupons are not enabled for this business yet." : msg);
    } finally {
      setCouponApplying(false);
    }
  };

  const getBookingDates = () => {
    if (!selectedDate || !selectedTime) return null;
    const [hours, minutes] = selectedTime.split(":").map(Number);
    const start = new Date(selectedDate);
    start.setHours(hours, minutes, 0, 0);
    const duration = multiServiceEnabled ? totalDurationMinutes || 30 : selectedService?.duration_minutes || 30;
    const end = addMinutes(start, duration);
    return { start, end };
  };

  const handleAddToCalendar = () => {
    const dt = getBookingDates();
    if (!dt || !business || (!multiServiceEnabled && !selectedService) || (multiServiceEnabled && selectedServices.length === 0)) {
      toast({ title: "Missing booking details", variant: "destructive" });
      return;
    }

    const title = multiServiceEnabled
      ? `${selectedServices.length} services — ${business.name}`
      : `${selectedService!.name} — ${business.name}`;
    const location = defaultLocation?.address || undefined;
    const detailsParts = [
      multiServiceEnabled
        ? `Services: ${selectedServices.map((s) => s.name).join(", ")}`
        : `Service: ${selectedService!.name}`,
      multiServiceEnabled ? `Staff: Any Available` : selectedStaff?.name ? `Staff: ${selectedStaff.name}` : null,
      confirmationCode ? `Reference Code: ${confirmationCode}` : null,
      location ? `Location: ${location}` : null,
      `Manage: ${getBaseUrl()}/book/${business.slug}/manage${confirmationCode ? `?code=${encodeURIComponent(confirmationCode)}` : ""}`,
    ].filter(Boolean) as string[];

    const uid = `${confirmationCode || Math.random().toString(36).slice(2)}@buukd`;
    downloadIcs({
      filename: `${business.slug}-booking-${confirmationCode || "appointment"}.ics`,
      title,
      description: detailsParts.join("\n"),
      location,
      startUtc: dt.start,
      endUtc: dt.end,
      uid,
    });

    toast({ title: "Calendar file downloaded", description: "Open it to add the appointment to your calendar." });
  };

  const googleCalendarUrl = () => {
    const dt = getBookingDates();
    if (!dt || !business || (!multiServiceEnabled && !selectedService) || (multiServiceEnabled && selectedServices.length === 0)) return null;
    const title = multiServiceEnabled
      ? `${selectedServices.length} services — ${business.name}`
      : `${selectedService!.name} — ${business.name}`;
    const location = defaultLocation?.address || undefined;
    const details = [
      confirmationCode ? `Reference Code: ${confirmationCode}` : null,
      multiServiceEnabled ? `Staff: Any Available` : selectedStaff?.name ? `Staff: ${selectedStaff.name}` : null,
    ].filter(Boolean).join("\n");
    return buildGoogleCalendarUrl({
      title,
      startUtc: dt.start,
      endUtc: dt.end,
      details: details || undefined,
      location,
    });
  };

  const handleConfirmBooking = async () => {
    if (!business) return;
    if (!customerDetails.name || !customerDetails.phone) return;
    if (emailValidationError) {
      setEmailTouched(true);
      toast({
        title: requiresEmailForOnlinePayment ? "Email required" : "Invalid email address",
        description: emailValidationError,
        variant: "destructive",
      });
      return;
    }
    if (!normalizedCustomerPhone) {
      setPhoneTouched(true);
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number before continuing.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    // Product-only order
    if (hasProductCart && !hasServiceSelection) {
      try {
        const items = Object.entries(productCart)
          .filter(([, q]) => q > 0)
          .filter(([productId]) => products.some((x) => x.id === productId))
          .map(([productId, quantity]) => ({
            product_id: productId,
            quantity,
            notes: null,
          }));
        if (items.length === 0) {
          throw new Error("Your cart is empty. Please add items before placing an order.");
        }

        const { data, error } = await (supabase as any).rpc("submit_product_order", {
          _business_slug: business.slug,
          _customer_name: customerDetails.name.trim(),
          _customer_phone: normalizedCustomerPhone,
          _customer_email: normalizedCustomerEmail,
          _fulfillment_type: productFulfillmentType,
          _delivery_address: productFulfillmentType === "delivery" ? productDeliveryAddress.trim() : null,
          _delivery_place_id: productFulfillmentType === "delivery" ? productDeliveryPlaceId || null : null,
          _delivery_lat: productFulfillmentType === "delivery" ? productDeliveryLat : null,
          _delivery_lng: productFulfillmentType === "delivery" ? productDeliveryLng : null,
          _items: items,
          _notes: customerDetails.notes?.trim() || null,
        });
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        setConfirmationCode(row?.confirmation_code || "");
        toast({
          title: "Order placed",
          description: `Your order code is ${row?.confirmation_code || ""}.`,
        });
        setProductCart({});
        goToStep("success");
        void (async () => {
          try {
            const orderId = row?.order_id ? String(row.order_id) : "";
            const code = row?.confirmation_code ? String(row.confirmation_code) : "";
            if (orderId && code) {
              await (supabase as any).functions.invoke("send-email", {
                body: { type: "product_order_created", orderId, confirmationCode: code },
              });
            }
          } catch {
            // ignore (email can be configured later)
          }
        })();
      } catch (e: any) {
        toast({
          title: "Order failed",
          description: e?.message || "Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!selectedDate || !selectedTime) return;

    try {
      const collectDepositsViaBuukd = businessUsesOnlineCheckout(business as any);
      const [hours, minutes] = selectedTime.split(":").map(Number);
      const start = new Date(selectedDate);
      start.setHours(hours, minutes, 0, 0);

      if (multiServiceEnabled) {
        if (selectedServiceIds.length === 0) return;

        const addonPayload = buildMultiServiceAddonPayload({
          serviceIds: selectedServiceIds,
          addonQtyByServiceId,
        });

        const { data, error } = await (supabase as any).rpc("create_multi_service_booking_public", {
          p_business_id: business.id,
          p_location_id: selectedLocationId || null,
          p_start_datetime: start.toISOString(),
          p_service_ids: selectedServiceIds,
          p_customer_name: customerDetails.name,
          p_customer_email: normalizedCustomerEmail,
          p_customer_phone: normalizedCustomerPhone,
          p_notes: customerDetails.notes || null,
          p_coupon_code: appliedCoupon?.code || null,
          p_addons: addonPayload,
        });
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        if (!row?.ok) {
          throw new Error(String(row?.error || "Failed to create booking"));
        }

        setConfirmationCode(String(row.confirmation_code || ""));
        setCreatedAppointmentId(String(row.appointment_id || ""));
        setCreatedAppointmentGroupId(String(row.appointment_group_id || ""));

        const selectedServiceNames = selectedServices
          .map((service) => String(service?.name || "").trim())
          .filter(Boolean);
        const bookingLabel =
          selectedServiceNames.length > 1
            ? `${selectedServiceNames[0]} + ${selectedServiceNames.length - 1} more`
            : selectedServiceNames[0] || "a service";

        await sendOwnerPush({
          businessId: business.id,
          title: "New appointment booked",
          body: `${customerDetails.name.trim()} booked ${bookingLabel} for ${format(start, "EEE, MMM d 'at' h:mm a")}.${requiresDeposit ? " Deposit required." : ""}`,
          url: "/app/appointments",
          data: {
            context: "appointment_created",
            context_id: String(row.appointment_id || ""),
            source: "online",
            confirmation_code: String(row.confirmation_code || ""),
            requires_deposit: requiresDeposit,
            appointment_group_id: String(row.appointment_group_id || ""),
          },
        });

        if (isExploreAppVariant()) {
          await notifyExploreCustomerBooking({
            businessName: business.name,
            businessSlug: business.slug,
            serviceLabel: bookingLabel,
            startDatetime: start,
            pendingPayment: (requiresDeposit || requiresReservationFee) && collectDepositsViaBuukd,
            confirmationCode: String(row.confirmation_code || ""),
          });
        } else {
          toast({
            title: "Booking created",
            description: `Your reference code is ${String(row.confirmation_code || "generated")}.`,
          });
        }

        // Email logic:
        // - No deposit / no reservation fee: send immediately (confirmation).
        // - Deposit paid directly to business: send immediately with instructions.
        // - Deposit or reservation via Buukd: send after Paystack verification (paystack-verify).
        if (
          ((!requiresDeposit && !requiresReservationFee) || collectDepositsViaBuukd === false) &&
          normalizedCustomerEmail &&
          row?.appointment_id &&
          row?.confirmation_code
        ) {
          try {
            await supabase.functions.invoke("send-email", {
              body: {
                type: "booking_created",
                appointmentId: String(row.appointment_id),
                confirmationCode: String(row.confirmation_code),
              },
            });
          } catch (e) {
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.warn("[BookingPage] Failed to send booking_created email (bundle):", e);
            }
          }
        }

        // Upload inspiration media (best-effort; do not block success)
        if (inspirationFiles.length > 0 && row?.appointment_id) {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
          if (supabaseUrl && anonKey) {
            try {
              const formData = new FormData();
              formData.set("appointment_id", String(row.appointment_id));
              inspirationFiles.forEach(({ file }) => formData.append("files", file));
              const res = await fetch(`${supabaseUrl}/functions/v1/upload-appointment-inspiration`, {
                method: "POST",
                headers: { Authorization: `Bearer ${anonKey}` },
                body: formData,
              });
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error((err as any)?.error || res.statusText);
              }
            } catch (e) {
              if (import.meta.env.DEV) console.warn("[BookingPage] Inspiration upload failed:", e);
              toast({
                title: "Inspiration not attached",
                description: "Your booking was confirmed. Attachment upload failed; you can share images with the business another way.",
                variant: "destructive",
              });
            }
          }
          inspirationFiles.forEach(({ objectUrl }) => objectUrl && URL.revokeObjectURL(objectUrl));
          setInspirationFiles([]);
        }

        // Deposit required + Buukd collects: redirect to Paystack immediately (pay before confirmation)
        if (requiresDeposit && collectDepositsViaBuukd) {
          try {
            const redirectPath = `/book/${business.slug}/manage${row?.confirmation_code ? `?code=${encodeURIComponent(row.confirmation_code)}` : ""}`;
            const callbackUrl = buildPaystackCallbackUrl(redirectPath);
            const { data, error } = await supabase.functions.invoke("paystack-init", {
              body: {
                context: { type: "appointment_group_deposit", appointmentGroupId: String(row.appointment_group_id) },
                payerEmail: normalizedCustomerEmail,
                payerName: customerDetails.name.trim() || null,
                callbackUrl,
              },
            });
            if (error) throw error;
            const url = String((data as any)?.authorizationUrl || "");
            if (!url) throw new Error("Missing Paystack authorizationUrl");
            toast({ title: "Redirecting to payment...", description: "Please complete payment to confirm your booking." });
            setTimeout(() => window.location.assign(url), 100);
            return;
          } catch (e: any) {
            toast({
              title: "Booking created",
              description: "Redirect to payment failed. Use the button below to pay the deposit.",
              variant: "destructive",
            });
          }
        }

        if (requiresReservationFee) {
          if (!normalizedCustomerEmail) {
            goToStep("success");
            return;
          }
          try {
            const redirectPath = `/book/${business.slug}/manage${row?.confirmation_code ? `?code=${encodeURIComponent(row.confirmation_code)}` : ""}`;
            const callbackUrl = buildPaystackCallbackUrl(redirectPath);
            const { data, error } = await supabase.functions.invoke("paystack-init", {
              body: {
                context: {
                  type: "appointment_group_reservation",
                  appointmentGroupId: String(row.appointment_group_id),
                },
                payerEmail: normalizedCustomerEmail,
                payerName: customerDetails.name.trim() || null,
                callbackUrl,
              },
            });
            if (error) throw error;
            const url = String((data as any)?.authorizationUrl || "");
            if (!url) throw new Error("Missing Paystack authorizationUrl");
            toast({ title: "Redirecting to payment...", description: "Pay the reservation fee to confirm your booking." });
            setTimeout(() => window.location.assign(url), 100);
            return;
          } catch {
            toast({
              title: "Booking created",
              description: "Redirect to payment failed. Use the button below to pay the reservation fee.",
              variant: "destructive",
            });
          }
        }

        goToStep("success");
        return;
      }

      if (!selectedService) return;
      const end = addMinutes(start, selectedService.duration_minutes || 30);

      // Create/Find customer via SECURITY DEFINER RPC (public booking; avoids RLS failures on upsert updates)
      const { data: customerId, error: customerError } = await (supabase as any).rpc(
        "find_or_create_customer_public",
        {
          p_business_id: business.id,
          p_name: customerDetails.name,
          p_email: normalizedCustomerEmail,
          p_phone: normalizedCustomerPhone,
          p_notes: customerDetails.notes || null,
        } as any
      );

      if (customerError) throw customerError;
      if (!customerId) throw new Error("Failed to create customer. Please try again.");

      // Create appointment with retry on confirmation_code collision
      let created: any = null;
      let lastError: any = null;
      for (let attempt = 0; attempt < 5; attempt++) {
        const code = generateConfirmationCode();
        const { data, error: aptError } = await supabase
          .from("appointments")
          .insert({
            business_id: business.id,
            service_id: selectedService.id,
            staff_id: selectedStaff?.id === "any" ? null : (selectedStaff?.id || null),
            location_id: selectedLocationId || null,
            customer_id: customerId,
            start_datetime: start.toISOString(),
            end_datetime: end.toISOString(),
            status: "pending",
            source: "online",
            notes: customerDetails.notes || null,
            confirmation_code: code,
            total_price_amount: totalAfterDiscount,
            coupon_id: appliedCoupon?.id || null,
            coupon_code: appliedCoupon?.code || null,
            discount_amount: discountAmount || 0,
            currency: selectedService.currency || business.currency || "GHS",
            explore_device_id: isExploreAppVariant() ? getExploreDeviceId() : null,
          } as any)
          .select()
          .single();

        if (!aptError) {
          created = data;
          setConfirmationCode(code);
          setCreatedAppointmentId(data?.id || null);

          // Store selected add-ons (non-blocking; booking can still succeed)
          if (data?.id && selectedAddons.length > 0) {
            const payload = selectedAddons.map((a) => ({
              appointment_id: data.id,
              addon_id: a.id,
              name: a.name,
              price_amount: a.price_amount,
              quantity: focusedAddonQtyById[a.id] || 1,
              currency: a.currency || selectedService.currency || business.currency || "GHS",
            }));
            const { error: addErr } = await supabase.from("appointment_addons").insert(payload as any);
            if (addErr) {
              console.error("Failed to save appointment add-ons:", addErr);
            }
          }
          break;
        }

        lastError = aptError;
        // Unique violation -> try again
        if (aptError?.code === "23505") continue;
        // Column missing (migration not run)
        if (aptError?.code === "PGRST204") {
          toast({
            title: "Database migration required",
            description: "Please run the confirmation_code migration in Supabase (ask me and I'll paste the SQL).",
            variant: "destructive",
          });
        }
        throw aptError;
      }

      if (!created) throw lastError || new Error("Failed to create booking. Please try again.");

      if (isExploreAppVariant()) {
        await notifyExploreCustomerBooking({
          businessName: business.name,
          businessSlug: business.slug,
          serviceLabel: selectedService.name,
          startDatetime: start,
          pendingPayment: (requiresDeposit || requiresReservationFee) && collectDepositsViaBuukd,
          confirmationCode: created?.confirmation_code ?? null,
        });
      } else {
        toast({
          title: "Booking created",
          description: `Your reference code is ${created?.confirmation_code || "generated"}. ${
            requiresDeposit
              ? collectDepositsViaBuukd
                ? "Please pay the deposit to confirm your appointment."
                : "Please follow the payment instructions to pay the deposit directly to the business."
              : requiresReservationFee
                ? "Pay the reservation fee to confirm your booking."
                : ""
          }`,
        });
      }

      await sendOwnerPush({
        businessId: business.id,
        title: "New appointment booked",
        body: `${customerDetails.name.trim()} booked ${selectedService.name} for ${format(start, "EEE, MMM d 'at' h:mm a")}.${requiresDeposit ? " Deposit required." : ""}`,
        url: "/app/appointments",
        data: {
          context: "appointment_created",
          context_id: created.id,
          source: "online",
          confirmation_code: created.confirmation_code,
          requires_deposit: requiresDeposit,
        },
      });

      // Email logic:
      // - No deposit / no reservation fee: send immediately (confirmation).
      // - Deposit paid directly to the business: send immediately with instructions (send-email reads business settings).
      // - Deposit or reservation via Buukd: send after Paystack verification (paystack-verify).
      if (
        ((!requiresDeposit && !requiresReservationFee) || !collectDepositsViaBuukd) &&
        normalizedCustomerEmail &&
        created?.id &&
        created?.confirmation_code
      ) {
        try {
          await supabase.functions.invoke("send-email", {
            body: {
              type: "booking_created",
              appointmentId: created.id,
              confirmationCode: created.confirmation_code,
            },
          });
        } catch (e) {
          // Best-effort; booking still succeeds.
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn("[BookingPage] Failed to send booking_created email:", e);
          }
        }
      }

      // Upload inspiration media for single-service booking (best-effort)
      if (inspirationFiles.length > 0 && created?.id) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        if (supabaseUrl && anonKey) {
          try {
            const formData = new FormData();
            formData.set("appointment_id", created.id);
            inspirationFiles.forEach(({ file }) => formData.append("files", file));
            const res = await fetch(`${supabaseUrl}/functions/v1/upload-appointment-inspiration`, {
              method: "POST",
              headers: { Authorization: `Bearer ${anonKey}` },
              body: formData,
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error((err as any)?.error || res.statusText);
            }
          } catch (e) {
            if (import.meta.env.DEV) console.warn("[BookingPage] Inspiration upload failed:", e);
            toast({
              title: "Inspiration not attached",
              description: "Your booking was confirmed. Attachment upload failed; you can share images with the business another way.",
              variant: "destructive",
            });
          }
        }
        inspirationFiles.forEach(({ objectUrl }) => objectUrl && URL.revokeObjectURL(objectUrl));
        setInspirationFiles([]);
      }

      // Deposit required + Buukd collects: redirect to Paystack immediately (pay before confirmation)
      if (requiresDeposit && collectDepositsViaBuukd) {
        if (!normalizedCustomerEmail) {
          goToStep("success");
          return;
        }
        try {
          const redirectPath = `/book/${business.slug}/manage${created?.confirmation_code ? `?code=${encodeURIComponent(created.confirmation_code)}` : ""}`;
          const callbackUrl = buildPaystackCallbackUrl(redirectPath);
          const { data, error } = await supabase.functions.invoke("paystack-init", {
            body: {
              context: { type: "appointment_deposit", appointmentId: created.id },
              payerEmail: normalizedCustomerEmail,
              payerName: customerDetails.name.trim() || null,
              callbackUrl,
            },
          });
          if (error) throw error;
          const url = String((data as any)?.authorizationUrl || "");
          if (!url) throw new Error("Missing Paystack authorizationUrl");
          toast({ title: "Redirecting to payment...", description: "Please complete payment to confirm your booking." });
          setTimeout(() => window.location.assign(url), 100);
          return;
        } catch (e: any) {
          toast({
            title: "Booking created",
            description: "Redirect to payment failed. Use the button below to pay the deposit.",
            variant: "destructive",
          });
        }
      }

      if (requiresReservationFee) {
        if (!normalizedCustomerEmail) {
          goToStep("success");
          return;
        }
        try {
          const redirectPath = `/book/${business.slug}/manage${created?.confirmation_code ? `?code=${encodeURIComponent(created.confirmation_code)}` : ""}`;
          const callbackUrl = buildPaystackCallbackUrl(redirectPath);
          const { data, error } = await supabase.functions.invoke("paystack-init", {
            body: {
              context: { type: "appointment_reservation", appointmentId: created.id },
              payerEmail: normalizedCustomerEmail,
              payerName: customerDetails.name.trim() || null,
              callbackUrl,
            },
          });
          if (error) throw error;
          const url = String((data as any)?.authorizationUrl || "");
          if (!url) throw new Error("Missing Paystack authorizationUrl");
          toast({ title: "Redirecting to payment...", description: "Pay the reservation fee to confirm your booking." });
          setTimeout(() => window.location.assign(url), 100);
          return;
        } catch (e: any) {
          toast({
            title: "Booking created",
            description: "Redirect to payment failed. Use the button below to pay the reservation fee.",
            variant: "destructive",
          });
        }
      }

    goToStep("success");
    } catch (err: any) {
      console.error("Booking submission error:", err);
      toast({
        title: "Booking failed",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background ios-safe-top">
        <header className="border-b border-border bg-card">
          <div className="container-app py-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">Back</span>
              </Link>
              <div className="h-4 w-px bg-border" />
              <h1 className="font-semibold text-foreground">Book an Appointment</h1>
            </div>
          </div>
        </header>
        <main className="container-app py-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading booking page...</p>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (error || !business) {
  return (
    <div className="min-h-screen bg-background ios-safe-top">
        <header className="border-b border-border bg-card">
          <div className="container-app py-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">Back</span>
              </Link>
              <div className="h-4 w-px bg-border" />
              <h1 className="font-semibold text-foreground">Book an Appointment</h1>
            </div>
          </div>
        </header>
        <main className="container-app py-12">
          <div className="flex flex-col items-center justify-center gap-4 text-center max-w-md mx-auto">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-semibold">Business Not Found</h2>
            <p className="text-muted-foreground">{error || "The business you're looking for doesn't exist or is no longer available."}</p>
            <Button asChild>
              <Link to="/">Go Home</Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const currencySymbol = getCurrencySymbol(business.currency || "USD");
  const productOrderCurrencySymbol = getCurrencySymbol(productCartCurrencies[0] || business.currency || "USD");
  const pageThemeStyle = (business as any)?.primary_color
    ? themeVarsFromHex((business as any).primary_color)
    : undefined;

  // Helper function to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    if (!hex || !hex.startsWith("#")) return undefined;
    const cleaned = hex.replace("#", "");
    if (cleaned.length !== 6) return undefined;
    const r = parseInt(cleaned.slice(0, 2), 16);
    const g = parseInt(cleaned.slice(2, 4), 16);
    const b = parseInt(cleaned.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const primaryColor = (business as any)?.primary_color;
  const termsBgColor = primaryColor ? hexToRgba(primaryColor, 0.08) : undefined;
  const termsBorderColor = primaryColor ? hexToRgba(primaryColor, 0.2) : undefined;
  const salesEnabled = (business as any)?.sales_enabled === true;
  const hasServiceSelection = multiServiceEnabled
    ? selectedServiceIds.length > 0
    : !!selectedService;
  const hasProductCart = salesEnabled && products.length > 0 && Object.keys(productCart).some((id) => (productCart[id] || 0) > 0);
  const hasSelection = hasServiceSelection || hasProductCart;
  const selectedServiceCount = selectedServices.length;
  const selectedServiceLabel = `${selectedServiceCount} ${selectedServiceCount === 1 ? "service" : "services"} selected`;
  const continueFromServiceStep = () => {
    if (hasProductCart && !hasServiceSelection) {
      goToStep("details");
      return;
    }
    if (mustSelectAddons) {
      const names = freeServicesMissingAddons.map((s) => s.name).join(", ");
      toast({
        title: "Add-on required",
        description:
          freeServicesMissingAddons.length === 1
            ? `"${names}" has add-ons. Please select at least one add-on to continue.`
            : `These services have add-ons: ${names}. Please select at least one add-on for each to continue.`,
        variant: "destructive",
      });
      return;
    }
    setSelectedServicesSummaryOpen(false);
    if (multiServiceEnabled) {
      setSelectedStaff({ id: "any", name: "Any Available" });
      goToStep("datetime");
    } else {
      goToStep("staff");
    }
  };

  const removeSelectedService = (serviceId: string) => {
    if (multiServiceEnabled) {
      setSelectedServiceIds((prev) => prev.filter((id) => id !== serviceId));
    } else if (selectedService?.id === serviceId) {
      setSelectedService(null);
    }

    if (selectedService?.id === serviceId) {
      setSelectedService(null);
    }

    setAddonQtyByServiceId((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
    setSelectedStaff(null);
    setSelectedDate(null);
    setSelectedTime(null);

    if (selectedServiceCount <= 1) {
      setSelectedServicesSummaryOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background ios-safe-top" style={pageThemeStyle}>
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container-app py-4">
          <div className="flex items-center">
            <div className="flex-1">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Go back"
              >
                <ChevronLeft className="h-5 w-5" />
                <span className="text-sm font-medium">Back</span>
              </button>
            </div>
            <h1 className="font-semibold text-foreground whitespace-nowrap">
              Book Appointment
            </h1>

            <div className="flex-1 flex justify-end pr-1 sm:pr-2">
              <SocialMediaIcons
                facebookUrl={(business as any)?.facebook_url}
                instagramUrl={(business as any)?.instagram_url}
                mainLabels={{
                  instagram: primaryLabelFor("instagram"),
                  facebook: primaryLabelFor("facebook"),
                  twitter: primaryLabelFor("twitter"),
                  linkedin: primaryLabelFor("linkedin"),
                  tiktok: primaryLabelFor("tiktok"),
                  whatsapp: primaryLabelFor("whatsapp"),
                }}
                twitterUrl={(business as any)?.twitter_url}
                linkedinUrl={(business as any)?.linkedin_url}
                tiktokUrl={(business as any)?.tiktok_url}
                whatsappUrl={(business as any)?.whatsapp_url}
                facebookUrls={extraAccountsFor("facebook")}
                instagramUrls={extraAccountsFor("instagram")}
                twitterUrls={extraAccountsFor("twitter")}
                linkedinUrls={extraAccountsFor("linkedin")}
                tiktokUrls={extraAccountsFor("tiktok")}
                whatsappUrls={extraAccountsFor("whatsapp")}
                size="sm"
                className="flex items-center gap-1"
              />
            </div>
          </div>
        </div>
      </header>

      <main
        className={cn(
          "container-app",
          step === "service" ? "pt-0 pb-8" : "py-8",
        )}
      >
        <div className="max-w-2xl mx-auto">
          {/* Business Header - Only show on first step */}
          {step === "service" && (
            <div className="mb-5 sm:mb-6">
              {/* Full-bleed hero (edge-to-edge, no rounded corners) */}
              <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
                <div className="relative bg-muted h-64 sm:h-80 md:h-96">
                  {heroMediaItems.length > 0 ? (
                    <div className="absolute inset-0">
                      <HeroCrossfadeMedia
                        key={`${business?.id ?? ""}-${heroMediaItems.map((m) => m.url).join("|")}`}
                        items={heroMediaItems}
                        index={heroIndex}
                        prefersReducedMotion={prefersReducedMotion}
                        heroHasMultiple={heroHasMultiple}
                        onVideoEnded={() => {
                          if (!heroHasMultiple) return;
                          setHeroIndex((prev) => (prev + 1) % heroMediaItems.length);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />
                  )}

                  {/* Contrast overlay */}
                  <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />

                  {/* Content (left aligned, Fresha-style) */}
                  <div className="absolute inset-0 z-20 px-5 sm:px-8 pb-5 sm:pb-6 flex flex-col justify-end">
                    <div className="flex items-end justify-between gap-4">
                      <div className="min-w-0 max-w-[44rem]">
                        <div
                          className={cn(
                            "inline-flex max-w-full items-center rounded-2xl bg-black/35 px-3 py-2.5 backdrop-blur-sm",
                            (business as any)?.logo_url || (extraLogos || []).some((l: any) => String(l?.url || "").trim())
                              ? "gap-3"
                              : "gap-0",
                          )}
                        >
                          {(business as any)?.logo_url || (extraLogos || []).some((l: any) => String(l?.url || "").trim()) ? (
                            <div className="shrink-0">
                              <LogoStack
                                primaryLogoUrl={String((business as any).logo_url || "")}
                                primaryLabel={`${business.name} logo`}
                                fallbackText={business.name.substring(0, 2).toUpperCase()}
                                logos={(extraLogos || []).map((l: any) => ({
                                  url: String(l.url || ""),
                                  label: String(l.label || ""),
                                }))}
                                size={44}
                                collapsedStep={18}
                                expandedGap={10}
                              />
                            </div>
                          ) : null}
                          <div className="min-w-0">
                            <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight drop-shadow-sm">
                              {business.name}
                            </h2>
                            <p className="mt-1 text-sm text-white/90 sm:text-base">
                              {business.description || "Book your appointment online, anytime"}
                            </p>
                          </div>
                        </div>
                      </div>

                  {heroHasMultiple ? (
                        <div className="hidden sm:flex items-center gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="bg-white/20 hover:bg-white/30 text-white border-white/20"
                        onClick={() =>
                          setHeroIndex((prev) => (prev - 1 + heroMediaItems.length) % heroMediaItems.length)
                        }
                            aria-label="Previous photo"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="bg-white/20 hover:bg-white/30 text-white border-white/20"
                        onClick={() => setHeroIndex((prev) => (prev + 1) % heroMediaItems.length)}
                            aria-label="Next photo"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Carousel indicator (centered + smaller) */}
                  {heroHasMultiple ? (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                      {heroMediaItems.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setHeroIndex(i)}
                          className={cn(
                            "h-1 rounded-full transition-all",
                            i === heroIndex
                              ? "w-4 bg-white/90"
                              : "w-2 bg-white/45 hover:bg-white/65",
                          )}
                          aria-label={`Go to photo ${i + 1}`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

            </div>
          )}
          {/* Progress Steps (service step renders this below the heading) */}
          {step !== "success" && step !== "service" && (
            <div className="flex items-center justify-center gap-2 mb-5 sm:mb-6">
              {(multiServiceEnabled
                ? ["service", "datetime", "details", "confirm"]
                : ["service", "staff", "datetime", "details", "confirm"]
              ).map((s, i, arr) => (
                <div key={s} className="flex items-center">
                  <div
                    className={cn(
                      "h-2 w-2 rounded-full",
                      step === s
                        ? "bg-primary"
                        : arr.indexOf(step) > i
                        ? "bg-primary"
                        : "bg-muted"
                    )}
                  />
                  {i < arr.length - 1 && <div className="w-8 h-0.5 bg-muted mx-1" />}
                </div>
              ))}
            </div>
          )}

          {/* Step: Select Service */}
          {step === "service" && (
            <div className="space-y-5">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-foreground">
                  {multiServiceEnabled ? "Select Services" : "Select a Service"}
                </h2>
                <p className="text-muted-foreground mt-1">
                  {multiServiceEnabled
                    ? "You can select one service per category"
                    : "Choose the service you'd like to book"}
                </p>
              </div>

              {/* Progress Steps (moved below the service heading) */}
              {hasSelection ? (
                <div className="flex items-center justify-center gap-2">
                  {(multiServiceEnabled
                    ? ["service", "datetime", "details", "confirm"]
                    : ["service", "staff", "datetime", "details", "confirm"]
                  ).map((s, i, arr) => (
                    <div key={s} className="flex items-center">
                      <div
                        className={cn(
                          "h-2 w-2 rounded-full",
                          step === s
                            ? "bg-primary"
                            : arr.indexOf(step) > i
                            ? "bg-primary"
                            : "bg-muted"
                        )}
                      />
                      {i < arr.length - 1 && <div className="w-8 h-0.5 bg-muted mx-1" />}
                    </div>
                  ))}
                </div>
              ) : null}

              {/* Location Selector */}
              {locations.length > 1 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="location-select" className="text-sm font-medium">Location</Label>
                    <span className="text-xs text-muted-foreground">
                      {locations.length} branches available
                    </span>
                  </div>
                  <Select
                    value={selectedLocationId || ""}
                    onValueChange={(value) => {
                      setSelectedLocationId(value);
                      // Reset selections when location changes
                      setSelectedService(null);
                      setSelectedServiceIds([]);
                      setAddonQtyByServiceId({});
                      setSelectedStaff(null);
                      setSelectedDate(null);
                      setSelectedTime(null);
                    }}
                  >
                    <SelectTrigger
                      id="location-select"
                      className="w-full h-auto min-h-[76px] rounded-2xl border-border bg-background px-4 py-3 shadow-md transition-all hover:border-primary/35 hover:bg-background focus:ring-primary/20"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-sm">
                          <MapPin className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 text-left">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                              Select branch
                            </span>
                            {(selectedLocation as any)?.is_default ? (
                              <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                                Default
                              </span>
                            ) : null}
                          </div>
                          <div className="truncate text-sm font-semibold text-foreground">
                            {selectedLocation?.name || "Select a location"}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {String((selectedLocation as any)?.address || "").trim() || "Choose where you want to visit"}
                          </div>
                        </div>
                      </div>
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-border bg-background p-2 shadow-2xl">
                      {locations.map((location) => (
                        <SelectItem
                          key={location.id}
                          value={location.id}
                          className="rounded-xl px-3 py-3 pl-8 pr-3 focus:bg-primary/10 focus:text-foreground data-[state=checked]:bg-primary/10 data-[state=checked]:text-foreground"
                        >
                          <div className="flex min-w-0 items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="truncate font-medium text-foreground">
                                  {location.name}
                                </span>
                                {(location as any).is_default ? (
                                  <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                                    Default
                                  </span>
                                ) : null}
                              </div>
                              {String((location as any)?.address || "").trim() ? (
                                <p className="mt-1 truncate text-xs text-muted-foreground">
                                  {String((location as any).address)}
                                </p>
                              ) : (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  Switch to this branch for available services and times
                                </p>
                              )}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {services.length === 0 && !(salesEnabled && products.length > 0) ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No services available at the moment.</p>
                  <p className="text-sm text-muted-foreground mt-2">Please check back later.</p>
                </div>
              ) : (
              <div className="space-y-4">
                {/* Category Filters */}
                {(serviceCategories.length > 0 ||
                  services.some((s: any) => !s.category_id) ||
                  (salesEnabled && products.length > 0)) && (
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedServiceCategory("all");
                      }}
                      className={cn(
                        "whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                        selectedServiceCategory === "all"
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-border hover:border-primary/50",
                      )}
                    >
                      All
                    </button>
                    {serviceCategories
                      .filter((c) => services.some((s: any) => String((s as any).category_id) === String(c.id)))
                      .map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setSelectedServiceCategory(c.id);
                            if (selectedService && String((selectedService as any).category_id) !== String(c.id)) {
                              setSelectedService(null);
                            }
                          }}
                          className={cn(
                            "whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                            selectedServiceCategory === c.id
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background border-border hover:border-primary/50",
                          )}
                        >
                          {c.name}
                        </button>
                      ))}
                    {services.some((s: any) => !s.category_id) && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedServiceCategory("uncategorized");
                          if (selectedService && (selectedService as any).category_id) setSelectedService(null);
                        }}
                        className={cn(
                          "whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                          selectedServiceCategory === "uncategorized"
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:border-primary/50",
                        )}
                      >
                        Other
                      </button>
                    )}
                    {salesEnabled && products.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedServiceCategory("shop");
                          setSelectedService(null);
                          setSelectedServiceIds([]);
                        }}
                        className={cn(
                          "whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                          selectedServiceCategory === "shop"
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:border-primary/50",
                        )}
                      >
                        Shop
                      </button>
                    )}
                  </div>
                )}

                {selectedServiceCategory === "shop" ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">Select products and adjust quantity with +/−</p>
                    <div className="grid gap-3">
                      {products.map((product) => {
                        const qty = productCart[product.id] || 0;
                        const isSoldOut = product.quantity_in_stock != null && product.quantity_in_stock <= 0;
                        const canDecrement = qty > 0;
                        const canIncrement = !isSoldOut && (product.quantity_in_stock == null || qty < product.quantity_in_stock);
                        const lineTotal = (Number(product.price_amount) || 0) * Math.max(0, qty);
                        const currencySym = getCurrencySymbol(product.currency || (business as any)?.currency || "GHS");
                        const productImageUrl = getProductImageUrl(product);
                        return (
                          <div
                            key={product.id}
                            className={cn(
                              "flex items-center gap-4 p-4 rounded-xl border text-left transition-all",
                              isSoldOut ? "border-border bg-muted/30 opacity-90" : qty > 0 ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                            )}
                          >
                            <div className="h-20 w-20 shrink-0 rounded-lg border border-border overflow-hidden bg-muted">
                              {productImageUrl ? (
                                <img
                                  src={productImageUrl}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center text-muted-foreground" aria-hidden>
                                  <Package className="h-8 w-8" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-foreground">{product.name}</h3>
                                {isSoldOut && (
                                  <span className="inline-flex items-center rounded-full bg-destructive/10 text-destructive px-2.5 py-0.5 text-xs font-medium border border-destructive/20">
                                    Sold out
                                  </span>
                                )}
                              </div>
                              {product.description ? (
                                <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{product.description}</p>
                              ) : null}
                              <p className="text-sm font-medium text-foreground mt-1">
                                {currencySym}{(Number(product.price_amount) || 0).toFixed(2)}
                              </p>
                              {qty > 0 && (
                                <p className="text-sm font-semibold text-primary mt-1">
                                  Line total: {currencySym}{lineTotal.toFixed(2)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {isSoldOut && qty === 0 ? (
                                <span className="text-sm text-muted-foreground font-medium">Sold out</span>
                              ) : (
                              <>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9"
                                disabled={!canDecrement}
                                onClick={() => setProductCart((prev) => {
                                  const next = { ...prev };
                                  const n = Math.max(0, (prev[product.id] || 0) - 1);
                                  if (n === 0) delete next[product.id];
                                  else next[product.id] = n;
                                  return next;
                                })}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="w-8 text-center font-medium">{qty}</span>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9"
                                disabled={!canIncrement}
                                onClick={() => setProductCart((prev) => ({
                                  ...prev,
                                  [product.id]: (prev[product.id] || 0) + 1,
                                }))}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                              </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : visibleServices.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No services in this category.</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {visibleServices.map((service) => {
                      const isSelected = multiServiceEnabled
                        ? selectedServiceIds.includes(service.id)
                        : selectedService?.id === service.id;
                      return (
                        <div key={service.id} id={`booking-service-${service.id}`} className="space-y-3">
                  <button
                    onClick={() => {
                      if (multiServiceEnabled) {
                        const alreadySelected = selectedServiceIds.includes(service.id);
                        const catKey = String((service as any)?.category_id || "uncategorized");
                        // Treat uncategorized as multi-select to avoid surprising replacements in "Other".
                        const isMultiSelectCategory =
                          catKey === "uncategorized" ? true : multiSelectCategoryIds.has(catKey);

                        // Focus only if selecting; if de-selecting, clear focus.
                        setSelectedService(alreadySelected ? null : service);

                        // If we're removing a selected service, also clear its add-on selections.
                        if (alreadySelected) {
                          setAddonQtyByServiceId((prev) => {
                            const next = { ...prev };
                            delete next[service.id];
                            return next;
                          });
                        }

                        // Multi-select categories: allow selecting more than one item in the same category.
                        if (isMultiSelectCategory) {
                          setSelectedServiceIds((prev) => {
                            const already = prev.includes(service.id);
                            if (already) return prev.filter((id) => id !== service.id);
                            return [...prev, service.id];
                          });
                        } else {
                        const selectedById = new Map(services.map((s) => [s.id, s] as const));
                        const existingSameCat = selectedServiceIds.find((sid) => {
                          const s = selectedById.get(sid);
                          const k = String((s as any)?.category_id || "uncategorized");
                          return k === catKey;
                        });

                        setSelectedServiceIds((prev) => {
                          const already = prev.includes(service.id);
                          if (already) return prev.filter((id) => id !== service.id);
                          const withoutSameCat = existingSameCat ? prev.filter((id) => id !== existingSameCat) : prev;
                          return [...withoutSameCat, service.id];
                        });

                        // If we replaced a same-category service, drop its add-ons selection too.
                        if (!alreadySelected && existingSameCat && existingSameCat !== service.id) {
                          setAddonQtyByServiceId((prev) => {
                            const next = { ...prev };
                            delete next[existingSameCat];
                            return next;
                          });
                          const replaced = selectedById.get(existingSameCat);
                          if (replaced?.name) {
                            toast({
                              title: "Switched service",
                              description: `Only one service per category is allowed. Replaced ${replaced.name} with ${service.name}.`,
                            });
                          }
                        }
                        }
                      } else {
                        const alreadySelected = selectedService?.id === service.id;
                        setSelectedService(alreadySelected ? null : service);
                        if (alreadySelected) {
                          setAddonQtyByServiceId((prev) => {
                            const next = { ...prev };
                            delete next[service.id];
                            return next;
                          });
                        }
                        setSelectedStaff(null);
                        setSelectedDate(null);
                        setSelectedTime(null);
                      }

                      // Any change to selected services should reset downstream selections
                      setSelectedStaff(null);
                      setSelectedDate(null);
                      setSelectedTime(null);
                    }}
                    className={cn(
                              "relative flex w-full items-center gap-4 p-4 rounded-xl border text-left transition-all overflow-hidden",
                              isSelected
                           ? "border-primary bg-primary/10"
                                : "border-border hover:border-primary/50 hover:bg-primary/5",
                            )}
                          >
                            {isSelected ? (
                              <div className="absolute right-3 top-3 h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                                <Check className="h-3.5 w-3.5" />
                              </div>
                            ) : null}
                            {/* Service Image or Video */}
                            {(() => {
                              const mediaItems = getServiceMediaItems(service);
                              if (mediaItems.length === 0) return null;

                              return (
                                <ServiceCardMedia
                                  mediaItems={mediaItems}
                             alt={service.name}
                                  onClick={() => {
                                    setGalleryMedia(mediaItems);
                                    setGalleryInitialIndex(0);
                                    setGalleryOpen(true);
                                  }}
                                />
                              );
                            })()}
                       
                       <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground">{service.name}</h3>
                         {service.description && (
                           <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{service.description}</p>
                         )}
                              {Number(service.duration_minutes || 0) > 0 ? (
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                                    {formatDuration(service.duration_minutes || 0)}
                        </span>
                      </div>
                              ) : null}
                    </div>
                       
                            {Number(service.price_amount || 0) > 0 && (
                       <div className="text-right flex-shrink-0">
                         <p className="font-semibold text-foreground">
                                  {currencySymbol}
                                  {Number(service.price_amount || 0).toFixed(2)}
                         </p>
                  </div>
                )}
                          </button>

                          {/* Add-ons (shown directly under the focused service) */}
                          {selectedService?.id === service.id &&
                          (
                            (addonsLoadingByServiceId[service.id] || false) ||
                            (Object.prototype.hasOwnProperty.call(addonsByServiceId || {}, service.id) &&
                              (addonsByServiceId[service.id] || []).length > 0)
                          ) ? (
                  <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-foreground">
                          {(Number(service.price_amount) || 0) === 0 &&
                          (addonsByServiceId[service.id] || []).length > 0
                            ? "Add-ons (required for this service)"
                            : "Optional add-ons"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(Number(service.price_amount) || 0) === 0 &&
                          (addonsByServiceId[service.id] || []).length > 0
                            ? "Please select at least one add-on to continue."
                            : "Enhance your service with extras (optional)."}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Total</p>
                        <p className="font-semibold text-foreground">
                          {currencySymbol}
                          {(
                            (Number(service.price_amount) || 0) +
                            (addonsByServiceId[service.id] || []).reduce((sum, a) => {
                              const qty = (addonQtyByServiceId[service.id] || {})[a.id] || 0;
                              return sum + (Number(a.price_amount) || 0) * qty;
                            }, 0)
                          ).toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {(addonsLoadingByServiceId[service.id] || false) ? (
                      <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading add-ons…
                      </div>
                    ) : (
                      <div className="grid gap-2">
                        {(addonsByServiceId[service.id] || []).map((addon) => {
                          const qty = (addonQtyByServiceId[service.id] || {})[addon.id] || 0;
                          const maxQty = Math.max(1, Number((addon as any)?.max_quantity ?? 1));
                          const isToggleStyle = maxQty <= 1;
                          return (
                            isToggleStyle ? (
                              <button
                                key={addon.id}
                                type="button"
                                onClick={() => {
                                  setAddonQtyByServiceId((prev) => {
                                    const cur = { ...(prev[service.id] || {}) };
                                    const nextQty = qty > 0 ? 0 : 1;
                                    if (nextQty === 0) delete cur[addon.id];
                                    else cur[addon.id] = 1;
                                    return { ...prev, [service.id]: cur };
                                  });
                                }}
                                className={cn(
                                  "flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors",
                                  qty > 0
                                    ? "border-primary bg-primary/10"
                                    : "border-border hover:bg-primary/5 hover:border-primary/50",
                                )}
                                aria-pressed={qty > 0}
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">{addon.name}</p>
                                  {Number(addon.price_amount || 0) > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                      +{currencySymbol}
                                      {Number(addon.price_amount || 0).toFixed(2)}
                                    </p>
                                  )}
                                </div>
                                <div className="flex-shrink-0">
                                  <span
                                    className={cn(
                                      "text-xs font-medium rounded-full px-2 py-1 border",
                                      qty > 0
                                        ? "border-primary/30 bg-primary/10 text-primary"
                                        : "border-border bg-background text-muted-foreground",
                                    )}
                                  >
                                    {qty > 0 ? "Added" : "Add"}
                                  </span>
                                </div>
                              </button>
                            ) : (
                              <div
                                key={addon.id}
                                className={cn(
                                  "flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors",
                                  qty > 0
                                    ? "border-primary bg-primary/10"
                                    : "border-border hover:bg-primary/5 hover:border-primary/50",
                                )}
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">{addon.name}</p>
                                  {Number(addon.price_amount || 0) > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                      +{currencySymbol}
                                      {Number(addon.price_amount || 0).toFixed(2)}
                                    </p>
                                  )}
                                </div>
                                <div className="flex-shrink-0">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      aria-label={`Decrease ${addon.name}`}
                                      disabled={qty <= 0}
                                      onClick={() => {
                                        setAddonQtyByServiceId((prev) => {
                                          const cur = { ...(prev[service.id] || {}) };
                                          const nextQty = Math.max(0, (cur[addon.id] || 0) - 1);
                                          if (nextQty === 0) {
                                            delete cur[addon.id];
                                          } else {
                                            cur[addon.id] = nextQty;
                                          }
                                          return { ...prev, [service.id]: cur };
                                        });
                                      }}
                                      className={cn(
                                        "h-8 w-8 rounded-full border flex items-center justify-center transition-colors",
                                        qty > 0 ? "border-primary/40 hover:bg-primary/10" : "border-border opacity-40 cursor-not-allowed",
                                      )}
                                    >
                                      <Minus className="h-4 w-4" />
                                    </button>

                                    <div className="w-6 text-center text-sm font-semibold text-foreground tabular-nums">{qty}</div>

                                    <button
                                      type="button"
                                      aria-label={`Increase ${addon.name}`}
                                      disabled={qty >= maxQty}
                                      onClick={() => {
                                        setAddonQtyByServiceId((prev) => {
                                          const cur = { ...(prev[service.id] || {}) };
                                          const nextQty = Math.min(maxQty, (cur[addon.id] || 0) + 1);
                                          if (nextQty <= 0) delete cur[addon.id];
                                          else cur[addon.id] = nextQty;
                                          return { ...prev, [service.id]: cur };
                                        });
                                      }}
                                      className={cn(
                                        "h-8 w-8 rounded-full border flex items-center justify-center transition-colors",
                                        qty >= maxQty ? "border-border opacity-40 cursor-not-allowed" : "border-primary/40 hover:bg-primary/10",
                                      )}
                                    >
                                      <Plus className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )
                          );
                                  })}
                                </div>
                              )}
                            </div>
                          ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

              <div className={cn("pt-2", hasServiceSelection ? "pb-24" : undefined)}>
                <Button className="w-full" disabled={!hasSelection} onClick={continueFromServiceStep}>
                  Continue
                </Button>
              </div>

              {hasServiceSelection && (
                <>
                  {!selectedServicesSummaryOpen ? (
                  <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-2">
                    <div className="pointer-events-auto mx-auto flex w-full max-w-2xl items-center gap-2 rounded-2xl border border-border/80 bg-background/95 p-2 shadow-[0_14px_45px_rgba(15,23,42,0.18)] backdrop-blur">
                      <button
                        type="button"
                        onClick={() => setSelectedServicesSummaryOpen(true)}
                        className="flex min-w-0 flex-1 items-center gap-2 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-muted/70 active:bg-muted"
                        aria-label={`Review ${selectedServiceLabel}`}
                        aria-expanded={selectedServicesSummaryOpen}
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                          {selectedServiceCount}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-foreground">
                            {selectedServicesBarTitle || (selectedServiceCount === 1 ? "Service selected" : "Services selected")}
                          </span>
                          <span className="block truncate text-xs font-medium text-muted-foreground">
                            {currencySymbol}
                            {totalPrice.toFixed(2)}
                          </span>
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </button>
                      <Button className="h-11 min-w-[8.25rem] shrink-0 rounded-xl px-5" onClick={continueFromServiceStep}>
                        Continue
                      </Button>
                    </div>
                  </div>
                  ) : null}

                  <Sheet open={selectedServicesSummaryOpen} onOpenChange={setSelectedServicesSummaryOpen}>
                    <SheetContent
                      side="bottom"
                      className="z-[100] max-h-[85vh] overflow-hidden rounded-t-2xl border-t p-0 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:max-w-xl sm:mx-auto"
                    >
                      <SheetHeader className="border-b border-border px-5 py-4 text-left">
                        <SheetTitle>Selected services</SheetTitle>
                        <SheetDescription>{selectedServiceLabel}. Review or remove services before continuing.</SheetDescription>
                      </SheetHeader>

                      <div className="max-h-[50vh] overflow-y-auto px-5 py-4">
                        <div className="space-y-3">
                          {selectedServices.map((service) => {
                            const serviceAddons = (addonsByServiceId[service.id] || []).filter(
                              (addon) => ((addonQtyByServiceId[service.id] || {})[addon.id] || 0) > 0,
                            );
                            const serviceAddonsTotal = serviceAddons.reduce((sum, addon) => {
                              const qty = (addonQtyByServiceId[service.id] || {})[addon.id] || 0;
                              return sum + (Number(addon.price_amount) || 0) * qty;
                            }, 0);
                            const serviceLineTotal = (Number(service.price_amount) || 0) + serviceAddonsTotal;

                            return (
                              <div key={service.id} className="rounded-xl border border-border bg-card p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="font-medium text-foreground">{service.name}</p>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                      {Number(service.duration_minutes || 0) > 0 ? (
                                        <span>{formatDuration(service.duration_minutes || 0)}</span>
                                      ) : null}
                                      <span>
                                        {currencySymbol}
                                        {serviceLineTotal.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 shrink-0 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() => removeSelectedService(service.id)}
                                  >
                                    Remove
                                  </Button>
                                </div>

                                {serviceAddons.length > 0 ? (
                                  <div className="mt-3 space-y-1 border-t border-border pt-3">
                                    {serviceAddons.map((addon) => {
                                      const qty = (addonQtyByServiceId[service.id] || {})[addon.id] || 0;
                                      return (
                                        <div key={addon.id} className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                                          <span className="truncate">
                                            {addon.name}
                                            {qty > 1 ? ` x${qty}` : ""}
                                          </span>
                                          <span className="shrink-0">
                                            +{currencySymbol}
                                            {((Number(addon.price_amount) || 0) * qty).toFixed(2)}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <SheetFooter className="border-t border-border px-5 py-4 sm:flex-col sm:space-x-0">
                        <div className="flex w-full flex-col gap-3">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Total</span>
                            <span className="font-semibold text-foreground">
                              {currencySymbol}
                              {totalPrice.toFixed(2)}
                            </span>
                          </div>
                          <Button className="w-full" disabled={!hasServiceSelection} onClick={continueFromServiceStep}>
                            Continue
                          </Button>
                        </div>
                      </SheetFooter>
                    </SheetContent>
                  </Sheet>
                </>
              )}
            </div>
          )}

          {/* Step: Select Staff */}
          {step === "staff" && !multiServiceEnabled && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-foreground">Select Staff</h2>
                <p className="text-muted-foreground mt-1">Choose who you'd like to see</p>
              </div>
              <div className="grid gap-3">
                {/* Any Available Option */}
                  <button
                    onClick={() => {
                    setSelectedStaff({ id: "any", name: "Any Available" });
                      goToStep("datetime");
                    }}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-xl border text-left transition-all",
                    selectedStaff?.id === "any"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50 hover:bg-primary/5"
                    )}
                  >
                    <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
                    <User className="h-5 w-5" />
                    </div>
                    <div>
                    <h3 className="font-medium text-foreground">Any Available</h3>
                        <p className="text-sm text-muted-foreground">First available staff member</p>
                  </div>
                </button>

                {/* Staff Members - Filtered by location and service assignment */}
                {staff
                  .filter((member) => {
                    // If no location selected, show all staff
                    if (!selectedLocationId) return true;
                    // If staff has no location assignments, they're available at all locations
                    const memberLocationIds = staffLocations[member.id] || [];
                    if (memberLocationIds.length === 0) return true;
                    // Otherwise, check if they're assigned to the selected location
                    return memberLocationIds.includes(selectedLocationId);
                  })
                  .filter((member) => {
                    // If a service is selected and it has specific staff assignments,
                    // only show staff linked to that service.
                    if (selectedService) {
                      const assignedStaffIds = serviceStaffMap[selectedService.id] || [];
                      if (assignedStaffIds.length > 0) {
                        return assignedStaffIds.includes(member.id);
                      }
                    }
                    // Otherwise, show all staff ( respects location filter above )
                    return true;
                  })
                  .map((member) => (
                  <button
                    key={member.id}
                    onClick={() => {
                      setSelectedStaff({ id: member.id, name: member.name, email: member.email || undefined });
                      goToStep("datetime");
                    }}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-xl border text-left transition-all",
                      selectedStaff?.id === member.id
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50 hover:bg-primary/5"
                    )}
                  >
                    <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">{member.name}</h3>
                      {member.email && (
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <Button variant="ghost" onClick={() => goToStep("service")} className="w-full">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back to services
              </Button>
            </div>
          )}

          {/* Step: Select Date & Time */}
          {step === "datetime" && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-foreground">Select Date & Time</h2>
                <p className="text-muted-foreground mt-1">Choose when you'd like to come in</p>
              </div>

              {/* Date Selection */}
               <div className="bg-card rounded-xl border border-border overflow-hidden p-4">
                {/** If business hours are configured but a specific day is missing, treat it as closed. */}
                 <Calendar
                   mode="single"
                   selected={selectedDate || undefined}
                  onSelect={handleSelectDate}
                  showOutsideDays={false}
                   disabled={(date) => {
                     // Disable past dates
                     if (isBefore(date, today)) return true;
                     
                     // Disable dates when business is closed
                     if (businessHours.length > 0) {
                       const dayOfWeek = date.getDay();
                       const dayHours = businessHours.find((h) => h.day_of_week === dayOfWeek);
                       // If hours are configured but this day isn't present, consider it closed.
                       if (!dayHours) return true;
                       if (dayHours.is_closed) return true;
                     }

                     if (selectedServices.length > 0 && !servicesAvailableOnWeekday(date.getDay())) {
                       return true;
                     }
                     
                     return false;
                   }}
                   className="w-full max-w-full [&_.rdp]:w-full [&_.rdp-months]:w-full [&_.rdp-months]:justify-center [&_.rdp-month]:w-full [&_.rdp-month]:max-w-[28rem] [&_.rdp-month]:mx-auto [&_.rdp-table]:w-full [&_table]:w-full [&_thead]:w-full [&_tbody]:w-full [&_tr]:grid [&_tr]:grid-cols-7 [&_tr]:w-full [&_td]:flex [&_td]:justify-center [&_th]:flex [&_th]:justify-center [&_.rdp-cell]:flex-1 [&_.rdp-day]:w-full [&_.rdp-day]:h-12 [&_.rdp-day]:text-base"
                 />
              </div>

              {/* Time Selection */}
              {selectedDate && (
                <div className="bg-card rounded-xl border border-border p-4">
                  <h3 className="font-medium text-foreground mb-4">
                    Available times for {format(selectedDate, "EEEE, MMMM d")}
                  </h3>
                  {appointmentsLoading || multiSlotsLoading ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <div
                          key={i}
                          className="py-2 px-3 rounded-lg bg-muted/50 animate-pulse"
                          style={{ height: '36px' }}
                        />
                      ))}
                    </div>
                  ) : timeSlots.length === 0 ? (
                    existingAppointments.length > 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        All available time slots for this day are booked. Please select another date.
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {(() => {
                          const dayOfWeek = selectedDate.getDay();
                          const dayHours = businessHours.find((h) => h.day_of_week === dayOfWeek);
                          const isClosed = !dayHours || !!dayHours.is_closed;

                          if (isClosed) return "No available times for this day. The business is closed.";
                          if (multiServiceEnabled) {
                            return "No available times for these services on this day. Try another date, or remove one service.";
                          }
                          return "No available times for this day.";
                        })()}
                      </p>
                    )
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {timeSlots.map((time) => {
                        // Check if this slot is available (not in conflict)
                        const isAvailable = true; // All slots in timeSlots are already filtered to be available
                        
                        return (
                          <button
                            key={time}
                            onClick={() => setSelectedTime(time)}
                            disabled={!isAvailable}
                            className={cn(
                              "py-2 px-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                              selectedTime === time
                                ? "bg-primary text-primary-foreground"
                                : isAvailable
                                ? "bg-muted hover:bg-primary/5 text-foreground"
                                : "bg-muted/50 text-muted-foreground cursor-not-allowed opacity-50"
                            )}
                          >
                            {format(new Date(`2000-01-01T${time}`), "h:mm a")}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={() => goToStep(multiServiceEnabled ? "service" : "staff")}
                  className="flex-1"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={() => goToStep("details")}
                  disabled={!selectedDate || !selectedTime}
                  className="flex-1"
                >
                  Continue
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step: Enter Details */}
          {step === "details" && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-foreground">Your Details</h2>
                <p className="text-muted-foreground mt-1">Tell us how to reach you</p>
              </div>

              <div className="bg-card rounded-xl border border-border p-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={customerDetails.name}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email {requiresEmailForOnlinePayment ? "*" : "(optional)"}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerDetails.email}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
                    onBlur={() => setEmailTouched(true)}
                    aria-invalid={shouldShowEmailValidationError}
                    placeholder="your@email.com"
                  />
                  {shouldShowEmailValidationError ? (
                    <p className="text-sm text-destructive">{emailValidationError}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Optional, but recommended for confirmations and online payments.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <div className="rounded-md border border-input bg-background px-3 py-2">
                    <PhoneInput
                      international
                      defaultCountry={defaultPhoneCountry}
                      value={customerDetails.phone || undefined}
                      onChange={(v) => setCustomerDetails({ ...customerDetails, phone: v || "" })}
                      onBlur={() => setPhoneTouched(true)}
                      placeholder="+233 24 141 3901"
                      numberInputProps={{
                        id: "phone",
                        name: "phone",
                        className: "w-full bg-transparent outline-none text-sm",
                        autoComplete: "tel",
                      }}
                    />
                  </div>
                  {phoneValidationError ? (
                    <p className="text-sm text-destructive">{phoneValidationError}</p>
                  ) : null}
                </div>
                {hasProductCart && (
                  <div className="space-y-4 rounded-xl border border-border p-4 bg-muted/20">
                    <Label className="text-sm font-medium">Fulfillment</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="fulfillment"
                          checked={productFulfillmentType === "pickup"}
                          onChange={() => setProductFulfillmentType("pickup")}
                          className="rounded-full border-border"
                        />
                        <span className="text-sm">Pickup</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="fulfillment"
                          checked={productFulfillmentType === "delivery"}
                          onChange={() => setProductFulfillmentType("delivery")}
                          className="rounded-full border-border"
                        />
                        <span className="text-sm">Delivery</span>
                      </label>
                    </div>
                    {productFulfillmentType === "delivery" && (
                      <div className="space-y-2">
                        <Label htmlFor="product-delivery-address">Delivery Address *</Label>
                        <PlacesAutocompleteInput
                          id="product-delivery-address"
                          value={productDeliveryAddress}
                          onValueChange={(v) => {
                            setProductDeliveryAddress(v);
                            setProductDeliveryPlaceId("");
                            setProductDeliveryLat(null);
                            setProductDeliveryLng(null);
                          }}
                          onPlaceSelected={(p) => {
                            setProductDeliveryAddress(p.formattedAddress || "");
                            setProductDeliveryPlaceId(p.placeId || "");
                            setProductDeliveryLat(typeof p.lat === "number" ? p.lat : null);
                            setProductDeliveryLng(typeof p.lng === "number" ? p.lng : null);
                          }}
                          placeholder="Enter your full address"
                          showUseMyLocation
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  {hasProductCart && !hasServiceSelection ? (
                    <>
                      <Label htmlFor="notes">Notes (optional)</Label>
                      <Textarea
                        id="notes"
                        value={customerDetails.notes}
                        onChange={(e) => setCustomerDetails({ ...customerDetails, notes: e.target.value })}
                        placeholder="Any notes for your order?"
                        rows={3}
                      />
                    </>
                  ) : (
                  <>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <Label htmlFor="notes">Notes (optional)</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Use the camera icon to upload inspo (images or videos).
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 shrink-0 rounded-full"
                      onClick={() => inspirationInputRef.current?.click()}
                      title="Upload inspo (images or videos)"
                    >
                      <Camera className="h-4 w-4" />
                    </Button>
                  </div>
                  <input
                    ref={inspirationInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const list = e.target.files ? Array.from(e.target.files) : [];
                      const maxNew = Math.max(0, 10 - inspirationFiles.length);
                      const toAdd = list.slice(0, maxNew).map((file) => {
                        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                        const objectUrl = file.type.startsWith("image/")
                          ? URL.createObjectURL(file)
                          : undefined;
                        return { id, file, objectUrl };
                      });
                      if (toAdd.length < list.length) {
                        toast({ title: "Limit reached", description: "You can add up to 10 images or videos.", variant: "destructive" });
                      }
                      setInspirationFiles((prev) => [...prev, ...toAdd]);
                      e.target.value = "";
                    }}
                  />
                  <Textarea
                    id="notes"
                    value={customerDetails.notes}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, notes: e.target.value })}
                    placeholder="Any special requests or notes? e.g., preferences, timing, or what you’re looking for"
                    rows={3}
                  />
                  {inspirationFiles.length > 0 ? (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {inspirationFiles.map(({ id, file, objectUrl }) => {
                        const isVideo = file.type.startsWith("video/");
                        return (
                          <div
                            key={id}
                            className="relative flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-2 pr-8"
                          >
                            {isVideo ? (
                              <span className="flex h-12 w-12 items-center justify-center rounded bg-muted text-muted-foreground">
                                <span className="text-xs font-medium">Video</span>
                              </span>
                            ) : (
                              objectUrl && (
                                <img
                                  src={objectUrl}
                                  alt=""
                                  className="h-12 w-12 rounded object-cover"
                                />
                              )
                            )}
                            <span className="max-w-[120px] truncate text-xs text-muted-foreground">{file.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-1 h-6 w-6"
                              onClick={() => {
                                if (objectUrl) URL.revokeObjectURL(objectUrl);
                                setInspirationFiles((p) => p.filter((x) => x.id !== id));
                              }}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  </>
                  )}
                </div>
              </div>

              {/* Terms and Conditions Section - only for appointments */}
              {!(hasProductCart && !hasServiceSelection) && (business as any)?.terms_and_conditions && (
                <div 
                  className="rounded-xl border p-4 space-y-3"
                  style={{
                    backgroundColor: termsBgColor,
                    borderColor: termsBorderColor,
                  }}
                >
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Terms and Conditions</Label>
                    <div className="max-h-32 overflow-y-auto text-sm font-medium text-foreground whitespace-pre-wrap">
                      {(business as any).terms_and_conditions}
                    </div>
                  </div>
                  <div 
                    className="flex items-start gap-3 pt-2 border-t" 
                    style={{ borderColor: termsBorderColor }}
                  >
                    <Checkbox
                      id="terms-agreement"
                      checked={agreedToTerms}
                      onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                      className="mt-0.5"
                      style={{
                        borderColor: primaryColor || undefined,
                      }}
                    />
                    <Label
                      htmlFor="terms-agreement"
                      className="text-sm font-normal cursor-pointer leading-relaxed"
                    >
                      I agree to the <span className="font-semibold">terms and conditions</span> above
                    </Label>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={() => goToStep(hasProductCart && !hasServiceSelection ? "service" : "datetime")}
                  className="flex-1"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={() => goToStep("confirm")}
                  disabled={
                    !customerDetails.name ||
                    !customerDetails.phone ||
                    !!emailValidationError ||
                    !!phoneValidationError ||
                    (hasProductCart &&
                      productFulfillmentType === "delivery" &&
                      !productDeliveryAddress.trim()) ||
                    (!(hasProductCart && !hasServiceSelection) && (business as any)?.terms_and_conditions && !agreedToTerms)
                  }
                  className="flex-1"
                >
                  Review {hasProductCart && !hasServiceSelection ? "Order" : "Booking"}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step: Confirm */}
          {step === "confirm" && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-foreground">
                  {hasProductCart && !hasServiceSelection ? "Confirm Order" : "Confirm Booking"}
                </h2>
                <p className="text-muted-foreground mt-1">
                  {hasProductCart && !hasServiceSelection ? "Review your order details" : "Review your appointment details"}
                </p>
              </div>

              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <div className="border-b border-border bg-background p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                    {hasProductCart && !hasServiceSelection ? "Order summary" : "Appointment summary"}
                  </p>
                  <h3 className="mt-2 text-xl font-bold text-foreground">
                    {hasProductCart && !hasServiceSelection ? "Review your order" : "Almost done"}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {hasProductCart && !hasServiceSelection
                      ? "Confirm your items before placing the order."
                      : "Confirm your service, time, and contact details."}
                  </p>
                </div>
                <div className="p-5 space-y-5">
                  {hasProductCart && !hasServiceSelection ? (
                    <>
                      <div className="rounded-2xl border border-border bg-background p-4">
                        <p className="text-sm font-semibold text-foreground">Products</p>
                        <div className="mt-2 space-y-2">
                          {Object.entries(productCart)
                            .filter(([, q]) => q > 0)
                            .map(([productId, quantity]) => {
                              const p = products.find((x) => x.id === productId);
                              if (!p) return null;
                              const lineTotal = (Number(p.price_amount) || 0) * quantity;
                              const lineCurrencySymbol = getCurrencySymbol(p.currency || business.currency || "USD");
                              return (
                                <div key={productId} className="flex items-start justify-between gap-3 rounded-xl bg-muted/40 px-3 py-2 text-sm">
                                  <span className="font-medium text-foreground">
                                    {p.name} <span className="text-muted-foreground">× {quantity}</span>
                                  </span>
                                  <span className="font-semibold text-foreground">
                                    {lineCurrencySymbol}{lineTotal.toFixed(2)}
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border bg-background p-4">
                        <p className="text-sm font-semibold text-foreground">Fulfillment</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {productFulfillmentType === "delivery" ? "Delivery" : "Pickup"}
                          {productFulfillmentType === "delivery" && productDeliveryAddress && ` • ${productDeliveryAddress}`}
                        </p>
                      </div>
                    </>
                  ) : multiServiceEnabled ? (
                    <>
                      <div className="rounded-2xl border border-border bg-background p-4">
                        <p className="text-sm font-semibold text-foreground">Services selected</p>
                        <p className="text-xs text-muted-foreground">
                          {selectedServices.length} {selectedServices.length === 1 ? "service" : "services"}
                        </p>
                        <div className="mt-3 space-y-2">
                          {selectedServices.map((s) => (
                            <div key={s.id} className="flex items-start justify-between gap-3 rounded-xl bg-muted/40 px-3 py-2 text-sm">
                              <span className="font-medium text-foreground">{s.name}</span>
                              {Number(s.price_amount || 0) > 0 ? (
                                <span className="font-semibold text-foreground">
                                  {currencySymbol}
                                  {Number(s.price_amount || 0).toFixed(2)}
                                </span>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>

                      {selectedServices.some((s) =>
                        (addonsByServiceId[s.id] || []).some(
                          (a) => ((addonQtyByServiceId[s.id] || {})[a.id] || 0) > 0,
                        ),
                      ) ? (
                        <div className="rounded-2xl border border-border bg-background p-4">
                          <p className="text-sm font-semibold text-foreground">Add-ons</p>
                          <div className="space-y-2">
                            {selectedServices.map((s) => {
                              const addons = addonsByServiceId[s.id] || [];
                              const qtyMap = addonQtyByServiceId[s.id] || {};
                              const picked = addons.filter((a) => (qtyMap[a.id] || 0) > 0);
                              if (picked.length === 0) return null;
                              return (
                                <div key={s.id} className="rounded-xl bg-muted/40 p-3">
                                  <p className="text-sm font-medium text-foreground">{s.name}</p>
                                  <div className="mt-2 space-y-1">
                                    {picked.map((a) => {
                                      const qty = qtyMap[a.id] || 0;
                                      const lineTotal = (Number(a.price_amount) || 0) * qty;
                                      return (
                                        <div key={a.id} className="flex items-center justify-between text-sm">
                                          <span className="text-foreground">
                                            {a.name} <span className="text-muted-foreground">× {qty}</span>
                                          </span>
                                          {lineTotal > 0 ? (
                                            <span className="text-muted-foreground">
                                              +{currencySymbol}
                                              {lineTotal.toFixed(2)}
                                            </span>
                                          ) : null}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <div className="rounded-2xl border border-border bg-background p-4">
                        <p className="text-sm font-semibold text-foreground">Service</p>
                        <div className="mt-3 flex justify-between items-start rounded-xl bg-muted/40 px-3 py-2">
                        <div>
                          <p className="font-medium text-foreground">{selectedService?.name}</p>
                          {Number(selectedService?.duration_minutes || 0) > 0 ? (
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {formatDuration(selectedService?.duration_minutes || 0)}
                            </p>
                          ) : null}
                        </div>
                        {serviceBase > 0 && (
                          <p className="font-semibold text-foreground">
                            {currencySymbol}
                            {serviceBase.toFixed(2)}
                          </p>
                        )}
                        </div>
                      </div>

                      {selectedAddons.length > 0 && (
                        <div className="rounded-2xl border border-border bg-background p-4">
                          <p className="text-sm font-semibold text-foreground">Add-ons</p>
                          <div className="space-y-1">
                            {selectedAddons.map((a) => {
                              const qty = focusedAddonQtyById[a.id] || 0;
                              const lineTotal = (Number(a.price_amount) || 0) * qty;
                              return (
                                <div key={a.id} className="flex items-center justify-between text-sm">
                                  <span className="text-foreground">
                                    {a.name} <span className="text-muted-foreground">× {qty}</span>
                                  </span>
                                  {lineTotal > 0 && (
                                    <span className="text-muted-foreground">
                                      +{currencySymbol}
                                      {lineTotal.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {!hasProductCart || hasServiceSelection ? (
                  <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-border bg-background p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <User className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Staff</p>
                          <p className="mt-1 font-semibold text-foreground">
                            {multiServiceEnabled ? "Any Available" : selectedStaff?.name}
                          </p>
                          {multiServiceEnabled ? (
                            <p className="mt-0.5 text-xs text-muted-foreground">Automatically assigned</p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-border bg-background p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Clock className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Date & time</p>
                          <p className="mt-1 font-semibold leading-snug text-foreground">
                            {selectedDate && format(selectedDate, "EEE, MMM d, yyyy")}
                          </p>
                          <p className="mt-0.5 text-sm text-muted-foreground">
                            {selectedTime ? format(new Date(`2000-01-01T${selectedTime}`), "h:mm a") : ""}
                            {Number((multiServiceEnabled ? totalDurationMinutes : selectedService?.duration_minutes) || 0) > 0
                              ? ` • ${formatDuration(multiServiceEnabled ? totalDurationMinutes : selectedService?.duration_minutes || 0)}`
                              : ""}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Coupon */}
                  <div className="rounded-2xl border border-border bg-background p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Coupon</p>
                        <p className="text-xs text-muted-foreground">Optional. Apply a promo code if you have one.</p>
                      </div>
                      {appliedCoupon ? (
                        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                          {appliedCoupon.code}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toUpperCase());
                          setCouponError(null);
                        }}
                        placeholder="Enter coupon code"
                        className="h-11 rounded-xl"
                        disabled={couponApplying}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 rounded-xl"
                        disabled={couponApplying || !couponCode.trim()}
                        onClick={applyCoupon}
                      >
                        {couponApplying ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Applying…
                          </>
                        ) : (
                          "Apply"
                        )}
                      </Button>
                      {appliedCoupon ? (
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-11 rounded-xl"
                          onClick={() => {
                            setAppliedCoupon(null);
                            setCouponCode("");
                            setCouponError(null);
                          }}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </div>
                    {couponError ? (
                      <p className="text-xs text-destructive mt-2">{couponError}</p>
                    ) : appliedCoupon ? (
                      <p className="text-xs text-muted-foreground mt-2">
                        Applied: <span className="font-semibold text-foreground">{appliedCoupon.code}</span>
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-2">Optional. If you have a code, apply it here.</p>
                    )}
                  </div>
                  {requiresDeposit && depositAmount > 0 ? (
                    <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                      <p className="mb-1 text-sm font-semibold text-foreground">Deposit required</p>
                      {collectDepositsViaBuukd && !onlinePaymentsAvailable ? (
                        <p className="mb-3 text-xs text-destructive">
                          Online payment is temporarily unavailable for this business. Please contact them to complete your booking.
                        </p>
                      ) : (
                      <p className="mb-3 text-xs text-muted-foreground">
                        {collectDepositsViaBuukd
                          ? "You’ll be asked to pay the deposit after confirming."
                          : "You’ll receive payment instructions after confirming."}
                      </p>
                      )}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-muted-foreground">Deposit</p>
                          <p className="text-sm font-medium text-foreground">
                            {currencySymbol}{Number(depositAmount || 0).toFixed(2)}
                          </p>
                        </div>
                        {collectDepositsViaBuukd && platformBookingFee > 0 ? (
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">Online payment fee</p>
                            <p className="text-sm font-medium text-foreground">
                              {currencySymbol}
                              {platformBookingFee.toFixed(2)}
                            </p>
                          </div>
                        ) : null}
                        <div className="flex items-center justify-between pt-2 border-t border-border">
                          <p className="text-sm font-semibold text-foreground">
                            {collectDepositsViaBuukd ? "Total to pay" : "Total to Pay"}
                          </p>
                          <p className="font-semibold text-foreground text-warning">
                            {currencySymbol}
                            {(collectDepositsViaBuukd ? depositPayTotal : Number(depositAmount || 0)).toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {collectDepositsViaBuukd
                          ? "You'll be asked to pay the deposit after confirming your booking."
                          : "You'll receive payment instructions after confirming your booking."}
                      </p>
                    </div>
                  ) : null}
                  {requiresReservationFee ? (
                    <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                      <p className="mb-3 text-xs text-muted-foreground">
                        You&apos;ll pay a small reservation fee online after confirming to secure your appointment. The service total is due at your visit.
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">Reservation fee</p>
                        <p className="font-semibold text-foreground">
                          {currencySymbol}
                          {reservationPayTotal.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ) : null}
                  </>
                  ) : null}
                  <div className="rounded-2xl border border-border bg-muted/30 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Subtotal</p>
                      <p className="text-sm font-medium text-foreground">
                        {(hasProductCart && !hasServiceSelection ? productOrderCurrencySymbol : currencySymbol)}
                        {(hasProductCart && !hasServiceSelection ? productCartTotal : totalPrice).toFixed(2)}
                      </p>
                    </div>
                    {(hasProductCart && !hasServiceSelection ? false : !!appliedCoupon) ? (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">Discount</p>
                        <p className="text-sm font-medium text-foreground">
                          -{currencySymbol}{discountAmount.toFixed(2)}
                      </p>
                    </div>
                  ) : null}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                      <p className="text-sm font-semibold text-foreground">Total</p>
                    <p className="font-semibold text-foreground">
                        {(hasProductCart && !hasServiceSelection ? productOrderCurrencySymbol : currencySymbol)}
                        {(hasProductCart && !hasServiceSelection ? productCartTotal : totalAfterDiscount).toFixed(2)}
                      </p>
                    </div>
                    {appliedCoupon && requiresDeposit && depositAmount > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        Note: the deposit amount stays the same. The discount applies to the remaining balance.
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="border-t border-border bg-muted/30 p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background text-primary shadow-sm">
                      <User className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Contact information</p>
                    <p className="mt-1 font-semibold text-foreground">{customerDetails.name}</p>
                    {normalizedCustomerEmail ? (
                      <p className="text-sm text-muted-foreground">{normalizedCustomerEmail}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">No email provided</p>
                    )}
                    <p className="text-sm text-muted-foreground">{customerDetails.phone}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="ghost" onClick={() => goToStep("details")} className="flex-1">
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={handleConfirmBooking}
                  className="flex-1"
                  disabled={
                    isSubmitting ||
                    ((requiresDeposit || requiresReservationFee) && collectDepositsViaBuukd && !onlinePaymentsAvailable)
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {hasProductCart && !hasServiceSelection ? "Placing order..." : "Confirming..."}
                    </>
                  ) : (
                    hasProductCart && !hasServiceSelection
                      ? "Place Order"
                      : requiresDeposit && collectDepositsViaBuukd
                        ? "Confirm & Pay Deposit"
                        : requiresReservationFee
                          ? "Confirm & Pay Reservation Fee"
                          : "Confirm Booking"
                  )}
                </Button>
              </div>
              {(business as any)?.cancellation_policy && (
                <p className="text-xs text-muted-foreground text-center mt-3">
                  {(business as any).cancellation_policy}
                </p>
              )}
            </div>
          )}

          {/* Step: Success */}
          {step === "success" && (
            <div className="space-y-6">
              <div className="h-16 w-16 rounded-full bg-warning/10 flex items-center justify-center mx-auto">
                <Check className="h-8 w-8 text-warning" />
              </div>
              <div className="text-center">
                {confirmationCode && !createdAppointmentId && !createdAppointmentGroupId ? (
                  <>
                    <h2 className="text-2xl font-bold text-foreground leading-tight">
                      Order Placed
                    </h2>
                    <p className="text-muted-foreground mt-2">
                      Your order has been placed. Keep your confirmation code safe to track your order.
                    </p>
                    <div className="mt-6 mx-auto inline-block rounded-2xl bg-[#F7F8F9] px-8 py-5 text-center min-w-[200px]">
                      <p className="text-xs font-normal text-[#667085]">Confirmation Code</p>
                      <p className="font-mono text-xl font-bold tracking-widest text-[#344054] mt-2">
                        {confirmationCode}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-foreground leading-tight">
                      {requiresDeposit || requiresReservationFee ? "Pending Confirmation" : "Booking Confirmed"}
                    </h2>
                    <p className="text-muted-foreground mt-2">
                      {requiresDeposit
                    ? collectDepositsViaBuukd
                    ? normalizedCustomerEmail
                      ? "Your appointment is booked. Pay the deposit to confirm, and we'll email you once payment is received."
                      : "Your appointment is booked. Add an email address before paying online, or contact the business for another deposit option."
                      : "Your appointment is booked and pending confirmation. Pay the deposit directly using the details below."
                    : requiresReservationFee
                      ? normalizedCustomerEmail
                        ? "Your appointment is booked. Pay the reservation fee to confirm, and we'll email you once payment is received."
                        : "Your appointment is booked. Add an email address before paying online."
                    : normalizedCustomerEmail
                      ? `Your appointment is booked. We've sent a confirmation to ${normalizedCustomerEmail}.`
                      : "Your appointment is booked. No email confirmation was sent because no email address was provided."}
                    </p>
                  </>
                )}
              </div>

              {/* Reservation fee payment */}
              {requiresReservationFee &&
              !requiresDeposit &&
              createdAppointmentId &&
              (multiServiceEnabled ? !!createdAppointmentGroupId : true) ? (
                <div className="bg-accent/50 rounded-lg border border-border p-4 space-y-3 text-left">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-foreground">Reservation fee</p>
                    <span className="font-semibold text-foreground text-lg">
                      {currencySymbol}
                      {reservationPayTotal.toFixed(2)}
                    </span>
                  </div>
                  <Button
                    className="w-full"
                    disabled={startingDepositPayment || !normalizedCustomerEmail}
                    onClick={async () => {
                      if (!normalizedCustomerEmail) {
                        toast({
                          title: "Email required",
                          description: "Add an email address to pay online.",
                          variant: "destructive",
                        });
                        return;
                      }
                      setStartingDepositPayment(true);
                      try {
                        const redirectPath = `/book/${business.slug}/manage${confirmationCode ? `?code=${encodeURIComponent(confirmationCode)}` : ""}`;
                        const callbackUrl = buildPaystackCallbackUrl(redirectPath);
                        const { data, error } = await supabase.functions.invoke("paystack-init", {
                          body: {
                            context: multiServiceEnabled
                              ? {
                                  type: "appointment_group_reservation",
                                  appointmentGroupId: String(createdAppointmentGroupId),
                                }
                              : { type: "appointment_reservation", appointmentId: createdAppointmentId },
                            payerEmail: normalizedCustomerEmail,
                            payerName: customerDetails.name.trim() || null,
                            callbackUrl,
                          },
                        });
                        if (error) throw error;
                        const url = String((data as any)?.authorizationUrl || "");
                        if (!url) throw new Error("Missing Paystack authorizationUrl");
                        setTimeout(() => {
                          window.location.assign(url);
                        }, 50);
                      } catch (e: any) {
                        toast({
                          title: "Payment failed",
                          description: e?.message || "Try again.",
                          variant: "destructive",
                        });
                      } finally {
                        setStartingDepositPayment(false);
                      }
                    }}
                  >
                    {startingDepositPayment ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Redirecting to Paystack…
                      </>
                    ) : (
                      <>
                        <Banknote className="mr-2 h-4 w-4" />
                        Pay reservation fee
                      </>
                    )}
                  </Button>
                </div>
              ) : null}

              {/* Deposit payment - moved to top for visibility */}
              {requiresDeposit &&
              createdAppointmentId &&
              (multiServiceEnabled ? !!createdAppointmentGroupId : true) &&
              collectDepositsViaBuukd ? (
                <div className="bg-accent/50 rounded-lg border border-border p-4 space-y-3 text-left">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-left">
                        <p className="font-medium text-foreground">Deposit Required</p>
                        <p className="text-sm text-muted-foreground">
                          Pay deposit to secure your booking
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1 pt-2 border-t border-border">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Deposit</span>
                        <span className="text-foreground">{currencySymbol}{Number(depositAmount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <span className="font-semibold text-foreground">Total to pay</span>
                        <span className="font-semibold text-foreground text-lg">
                          {currencySymbol}{depositPayTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    className="w-full"
                    disabled={startingDepositPayment || !normalizedCustomerEmail}
                    onClick={async () => {
                      if (!normalizedCustomerEmail) {
                        toast({
                          title: "Email required",
                          description: "Add an email address to pay your deposit online.",
                          variant: "destructive",
                        });
                        return;
                      }
                      setStartingDepositPayment(true);
                      try {
                        const redirectPath = `/book/${business.slug}/manage${confirmationCode ? `?code=${encodeURIComponent(confirmationCode)}` : ""}`;
                        const callbackUrl = buildPaystackCallbackUrl(redirectPath);
                        const { data, error } = await supabase.functions.invoke("paystack-init", {
                          body: {
                            context: multiServiceEnabled
                              ? { type: "appointment_group_deposit", appointmentGroupId: String(createdAppointmentGroupId) }
                              : { type: "appointment_deposit", appointmentId: createdAppointmentId },
                            payerEmail: normalizedCustomerEmail,
                            payerName: customerDetails.name.trim() || null,
                            callbackUrl,
                          },
                        });
                        if (error) throw error;
                        const url = String((data as any)?.authorizationUrl || "");
                        if (!url) throw new Error("Missing Paystack authorizationUrl");
                        
                        // Safari mobile fix: Use assign() with a small delay to prevent blocking
                        setTimeout(() => {
                          window.location.assign(url);
                        }, 50);
                      } catch (e: any) {
                        toast({
                          title: "Failed to start deposit payment",
                          description: e?.message || "Try again.",
                          variant: "destructive",
                        });
                      } finally {
                        setStartingDepositPayment(false);
                      }
                    }}
                  >
                    {startingDepositPayment ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Redirecting to Paystack…
                      </>
                    ) : (
                      <>
                        <Banknote className="mr-2 h-4 w-4" />
                        Pay Deposit Now
                      </>
                    )}
                  </Button>
                  {!normalizedCustomerEmail ? (
                    <p className="text-xs text-muted-foreground">
                      Email is needed only for online payment links.
                    </p>
                  ) : null}

                  {allowFullPaymentAtBooking ? (
                    <div className="mt-4 rounded-lg border border-border bg-background p-4 space-y-2 text-left">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">Or pay full amount now</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Pay the full amount upfront so there’s no remaining balance at your visit.
                          </p>
                        </div>
                      </div>
                      <div className="space-y-1 pt-2 border-t border-border">
                        <div className="flex items-center justify-between pt-2 border-t border-border">
                          <span className="font-semibold text-foreground">Total to pay</span>
                          <span className="font-semibold text-foreground text-lg">
                            {currencySymbol}{fullPayTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        disabled={startingDepositPayment || !normalizedCustomerEmail}
                        onClick={async () => {
                          if (!normalizedCustomerEmail) {
                            toast({
                              title: "Email required",
                              description: "Add an email address to pay online.",
                              variant: "destructive",
                            });
                            return;
                          }
                          setStartingDepositPayment(true);
                          try {
                            const redirectPath = `/book/${business.slug}/manage${confirmationCode ? `?code=${encodeURIComponent(confirmationCode)}` : ""}`;
                            const callbackUrl = buildPaystackCallbackUrl(redirectPath);
                            const { data, error } = await supabase.functions.invoke("paystack-init", {
                              body: {
                                context: { type: "appointment_balance", appointmentId: createdAppointmentId },
                                payerEmail: normalizedCustomerEmail,
                                payerName: customerDetails.name.trim() || null,
                                callbackUrl,
                              },
                            });
                            if (error) throw error;
                            const url = String((data as any)?.authorizationUrl || "");
                            if (!url) throw new Error("Missing Paystack authorizationUrl");
                            setTimeout(() => {
                              window.location.assign(url);
                            }, 50);
                          } catch (e: any) {
                            toast({
                              title: "Failed to start payment",
                              description: e?.message || "Try again.",
                              variant: "destructive",
                            });
                          } finally {
                            setStartingDepositPayment(false);
                          }
                        }}
                      >
                        {startingDepositPayment ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Redirecting to Paystack…
                          </>
                        ) : (
                          <>
                            <Banknote className="mr-2 h-4 w-4" />
                            Pay Full Amount Now
                          </>
                        )}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Direct payment instructions (when Buukd is not collecting deposits) */}
              {requiresDeposit && !collectDepositsViaBuukd ? (
                <div className="bg-accent/50 rounded-lg border border-border p-4 text-left">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">Pay deposit directly</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Follow these 3 steps to confirm your booking.
                      </p>
                    </div>
                  </div>

                  <ol className="mt-3 space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-background border border-border flex items-center justify-center text-xs font-semibold text-foreground">
                        1
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">
                          Send{" "}
                          <span className="font-semibold">
                            {currencySymbol}
                            {Number(depositAmount || 0).toFixed(2)}
                          </span>
                        </p>
                      </div>
                    </li>

                    <li className="flex items-start gap-3">
                      <div className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-background border border-border flex items-center justify-center text-xs font-semibold text-foreground">
                        2
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">
                          Use reference{" "}
                          <span className="font-mono font-semibold">
                            {confirmationCode || "—"}
                          </span>
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!confirmationCode}
                        onClick={() =>
                          copyToClipboard(String(confirmationCode || ""), "Reference copied")
                        }
                      >
                        Copy
                      </Button>
                    </li>

                    <li className="flex items-start gap-3">
                      <div className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-background border border-border flex items-center justify-center text-xs font-semibold text-foreground">
                        3
                      </div>
                      <div className="min-w-0 flex-1">
                        {directDepositPhone ? (
                          <p className="text-sm font-medium text-foreground">
                            Pay to{" "}
                            <span className="font-mono font-semibold">
                              {directDepositPhone}
                            </span>
                            {directDepositName ? (
                              <span className="text-muted-foreground font-normal">
                                {" "}
                                ({directDepositName})
                              </span>
                            ) : null}
                          </p>
                        ) : (
                          <p className="text-sm font-medium text-foreground">
                            Pay using the business details
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!directDepositPhone}
                        onClick={() => copyToClipboard(directDepositPhone, "Number copied")}
                      >
                        Copy
                      </Button>
                    </li>
                  </ol>

                  {directDepositInstructions ? (
                    <details className="mt-3">
                      <summary className="text-sm text-primary cursor-pointer hover:underline">
                        Show payment instructions
                      </summary>
                      <pre className="mt-2 whitespace-pre-wrap break-words text-sm text-foreground bg-background border border-border rounded-lg p-3">
                        {directDepositInstructions}
                      </pre>
                    </details>
                  ) : (
                    <p className="mt-3 text-sm text-muted-foreground">
                      Payment instructions are not set. Please contact the business.
                    </p>
                  )}
                </div>
              ) : null}

              {confirmationCode && !createdAppointmentId && !createdAppointmentGroupId ? null : (
              <div className="bg-card rounded-xl border border-border p-6 text-left">
                <h3 className="font-semibold text-foreground mb-4 text-center">Appointment Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Service</span>
                    <span className="font-medium text-foreground">{selectedService?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date</span>
                    <span className="font-medium text-foreground">
                      {selectedDate && format(selectedDate, "MMMM d, yyyy")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time</span>
                    <span className="font-medium text-foreground">{selectedTime ? format(new Date(`2000-01-01T${selectedTime}`), "h:mm a") : ""}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Staff</span>
                    <span className="font-medium text-foreground">{selectedStaff?.name}</span>
                  </div>
                  {defaultLocation?.address && (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-muted-foreground">Location</p>
                          <p className="font-medium text-foreground break-words">
                            {defaultLocation.address}
                          </p>
                        </div>
                        <a
                          className="text-sm text-primary hover:underline whitespace-nowrap"
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            defaultLocation.address,
                          )}${
                            (defaultLocation as any)?.place_id
                              ? `&query_place_id=${encodeURIComponent((defaultLocation as any).place_id)}`
                              : ""
                          }`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Directions
                        </a>
                      </div>
                      <MapPreview
                        lat={
                          typeof (defaultLocation as any)?.latitude === "number"
                            ? (defaultLocation as any).latitude
                            : null
                        }
                        lng={
                          typeof (defaultLocation as any)?.longitude === "number"
                            ? (defaultLocation as any).longitude
                            : null
                        }
                        placeId={String((defaultLocation as any)?.place_id || "") || null}
                        address={String((defaultLocation as any)?.address || "") || null}
                        className="h-[180px] w-full rounded-lg border border-border overflow-hidden"
                      />
                    </div>
                  )}
                  {confirmationCode && (
                    <div className="mt-6 space-y-2">
                      <div className="rounded-2xl bg-[#F7F8F9] px-8 py-5 text-center">
                        <p className="text-xs font-normal text-[#667085]">Confirmation Code</p>
                        <p className="font-mono text-xl font-bold tracking-widest text-[#344054] mt-2">
                          {confirmationCode}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        Keep this code safe. You’ll need it to reschedule or cancel.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              )}

              <div className="flex flex-col gap-3">
                <Button asChild className="w-full">
                  <Link
                    to={`/book/${business.slug}/manage${confirmationCode ? `?code=${encodeURIComponent(confirmationCode)}` : ""}`}
                  >
                    {confirmationCode && !createdAppointmentId && !createdAppointmentGroupId ? "Manage Order" : "Manage Booking"}
                  </Link>
                </Button>
                {!(confirmationCode && !createdAppointmentId && !createdAppointmentGroupId) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button variant="outline" className="w-full" onClick={handleAddToCalendar}>
                    Download .ics 
                  </Button>
                  {googleCalendarUrl() ? (
                    <Button asChild variant="outline" className="w-full">
                      <a href={googleCalendarUrl()!} target="_blank" rel="noreferrer">
                        Add to Google Calendar
                      </a>
                    </Button>
                  ) : (
                    <Button variant="outline" className="w-full" disabled>
                      Add to Google Calendar
                </Button>
                  )}
                </div>
                )}
                <Button asChild variant="ghost" className="w-full">
                  <Link to="/">Back to Home</Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Service Gallery Modal */}
      <ServiceGallery
        mediaItems={galleryMedia}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        initialIndex={galleryInitialIndex}
      />
    </div>
  );
}
